import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import "./ArbeitsnachweisPa.css";

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember"
];
const DAYS_DE = ["So","Mo","Di","Mi","Do","Fr","Sa"];

// Zeit robust auf "HH:MM" bringen (auch "8:30:00" → "08:30", nie "8:30:")
const toHHMM = (t) => {
  if (!t) return "";
  const p = String(t).split(":");
  if (p.length >= 2) return `${String(p[0]).padStart(2, "0")}:${String(p[1]).padStart(2, "0")}`;
  return String(t).slice(0, 5);
};

const ART_BADGE = {
  Arbeit:     { cls: "art-arbeit",     label: "Arbeit" },
  Ist:        { cls: "art-arbeit",     label: "Ist" },
  Plan:       { cls: "art-plan",       label: "Plan" },
  Krank:      { cls: "art-krank",      label: "Krank" },
  Urlaub:     { cls: "art-urlaub",     label: "Urlaub" },
  Feiertag:   { cls: "art-feiertag",   label: "Feiertag" },
  Wochenende: { cls: "art-wochenende", label: "WE" },
  Release:    { cls: "art-release",    label: "Release" },
};

const TOKEN_KEY = "maToken";  // muss mit AuthContext übereinstimmen
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

/** Bundesländer Deutschland (Kürzel → Vollname). Sortiert nach Vollnamen
 *  für eine intuitive Auswahl im Admin-Modal. */
const BUNDESLAENDER = {
  BW: "Baden-Württemberg",
  BY: "Bayern",
  BE: "Berlin",
  BB: "Brandenburg",
  HB: "Bremen",
  HH: "Hamburg",
  HE: "Hessen",
  MV: "Mecklenburg-Vorpommern",
  NI: "Niedersachsen",
  NW: "Nordrhein-Westfalen",
  RP: "Rheinland-Pfalz",
  SL: "Saarland",
  SN: "Sachsen",
  ST: "Sachsen-Anhalt",
  SH: "Schleswig-Holstein",
  TH: "Thüringen",
};

/** ISO-Wochennummer (Mo-So); Rückgabe: [iso_year, iso_week]. */
function getISOWeek(dt) {
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return [d.getUTCFullYear(), week];
}

/** Mo-Datum einer ISO-Woche. */
function isoWeekMonday(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const isoWeek1Monday = new Date(jan4);
  isoWeek1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  const monday = new Date(isoWeek1Monday);
  monday.setUTCDate(isoWeek1Monday.getUTCDate() + (week - 1) * 7);
  return monday;
}

/**
 * Tage-Tabelle — wiederverwendet vom „Tage"-Tab des Users und vom
 * Admin-Drill-Down. Hat optional onEdit + Release-Detail-Hook.
 */
function TagesSumme({ eintraege, regelarbeitszeit_h }) {
  const regel = parseFloat(regelarbeitszeit_h) || 0;
  const heute = new Date().toISOString().slice(0, 10);
  // Nur vergangene + heutige Tage zählen — zukünftige Einträge sind nur Vormerkung.
  const past = (eintraege || []).filter(e => (e.datum || "").slice(0, 10) <= heute);
  const future = (eintraege || []).length - past.length;
  const s = past.reduce((acc, e) => {
    if (e.art === "Arbeit" || e.art === "Ist" || e.art === "Plan") {
      acc.arbeit_tage += 1;
      acc.arbeit_h += parseFloat(e.arbeitszeit_h) || 0;
    } else if (e.art === "Urlaub")   acc.urlaub += 1;
    else if (e.art === "Krank")      acc.krank += 1;
    else if (e.art === "Feiertag")   acc.feiertag += 1;
    else if (e.art === "Wochenende") acc.we += 1;
    (e.zusatz || []).forEach(z => { acc.arbeit_h += parseFloat(z.arbeitszeit_h) || 0; });
    return acc;
  }, { arbeit_tage:0, arbeit_h:0, urlaub:0, krank:0, feiertag:0, we:0 });
  const bezahlte_tage = s.urlaub + s.krank + s.feiertag;
  const bezahlt_h = bezahlte_tage * regel;
  const total_h = s.arbeit_h + bezahlt_h;
  if (!eintraege || eintraege.length === 0) return null;
  return (
    <div className="tages-summe">
      <div className="tages-summe-haupt">
        <span className="tages-summe-label">Σ Stunden</span>
        <span className="tages-summe-val">{total_h.toFixed(1)} h</span>
      </div>
      <div className="tages-summe-detail">
        <span>{s.arbeit_tage} Arbeit ({s.arbeit_h.toFixed(1)} h)</span>
        {s.urlaub > 0   && <span>· {s.urlaub} Urlaub</span>}
        {s.krank > 0    && <span>· {s.krank} Krank</span>}
        {s.feiertag > 0 && <span>· {s.feiertag} Feiertag</span>}
        {bezahlte_tage > 0 && regel > 0 && (
          <span className="tages-summe-zusatz">+ {bezahlt_h.toFixed(1)} h bezahlt (U/K/F)</span>
        )}
        {future > 0 && (
          <span className="tages-summe-zusatz">· {future} vorgemerkt (noch nicht gezählt)</span>
        )}
      </div>
    </div>
  );
}

// Sanfter Lade-Platzhalter (Skeleton) statt nacktem „Lade …"-Text.
function TageSkeleton({ rows = 8 }) {
  return (
    <div className="tbl-wrap anz-skeleton" aria-busy="true" aria-label="Lädt …">
      <table className="tbl">
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="sk-row">
              {Array.from({ length: 7 }).map((__, j) => (
                <td key={j}><span className="sk-bar" style={{ width: `${[55,40,45,35,35,30,70][j]}%` }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TagesTable({ eintraege, loading, expandedDays, toggleDayExpand,
                       onEdit, onReleaseClick, regelarbeitszeit_h,
                       onAddTaetigkeit, onKopieren, onDelete,
                       copyMode, copyModus, copyApplied, onCopyTarget, onExitCopy,
                       onConfirmCopy, copySaving }) {
  const fmtTime = (t) => t ? toHHMM(t) : "–";
  const regel = parseFloat(regelarbeitszeit_h) || null;
  // Datum inkl. Wochentag anzeigen (Mo/Di/… aus dem Datum berechnet).
  const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const datumLabel = (datum) => {
    if (!datum) return "–";
    const iso = String(datum).slice(0, 10);
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d)) return iso;
    const [y, m, dd] = iso.split("-");
    return `${WD[d.getDay()]} · ${dd}.${m}.${y}`;
  };
  // Zusatzfelder (Tätigkeit/Epic) können in der DB-Spalte ODER in felder_json.extra
  // liegen (Zeiterfassung speichert sie in felder_json). Beides berücksichtigen.
  const extraOf = (e) => {
    let fj = e.felder_json;
    if (typeof fj === "string") { try { fj = JSON.parse(fj); } catch { return {}; } }
    return (fj && fj.extra) || {};
  };
  const epicOf = (e) => e.epic || extraOf(e).epic || "";
  const taetOf = (e) => e.taetigkeit || extraOf(e).taetigkeit || "";
  // Pause (TIME "00:30:00" oder null) → "0:30" / "–"
  const fmtPause = (p) => {
    if (!p) return "–";
    const parts = String(p).split(":");
    if (parts.length < 2) return "–";
    const h = parseInt(parts[0], 10), m = parseInt(parts[1], 10);
    if (!h && !m) return "–";
    return `${h}:${String(m).padStart(2, "0")}`;
  };
  const stdAnzeige = (e) => {
    if (e.arbeitszeit_h != null) return `${e.arbeitszeit_h} h`;
    if (regel && (e.art === "Urlaub" || e.art === "Krank" || e.art === "Feiertag")) {
      return `${regel} h`;
    }
    return "–";
  };
  // Einträge nach Tag gruppieren: erster Eintrag = Haupt-Zeile, weitere = Zusatz (einklappbar)
  const gruppen = [];
  const idxByDay = {};
  for (const e of (eintraege || [])) {
    if (idxByDay[e.datum] === undefined) {
      idxByDay[e.datum] = gruppen.length;
      gruppen.push({ datum: e.datum, main: e, extra: [] });
    } else {
      gruppen[idxByDay[e.datum]].extra.push(e);
    }
  }
  const nCols = onEdit ? 11 : 10;
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <colgroup>
          <col style={{width:"13%"}} />
          <col style={{width:"7%"}} />
          <col style={{width:"9%"}} />
          <col style={{width:"6%"}} />
          <col style={{width:"6%"}} />
          <col style={{width:"5%"}} />
          <col style={{width:"6%"}} />
          <col style={{width:onEdit ? "16%" : "20%"}} />
          <col style={{width:"8%"}} />
          <col style={{width:onEdit ? "10%" : "14%"}} />
          {onEdit && <col style={{width:"9%"}} />}
        </colgroup>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Art</th>
            <th>Ort</th>
            <th className="r">Von</th>
            <th className="r">Bis</th>
            <th className="r">Pause</th>
            <th className="r">Std.</th>
            <th>Was</th>
            <th>Tätigkeit</th>
            <th>Epic/Thema</th>
            {onEdit && <th></th>}
          </tr>
        </thead>
        <tbody>
          {(!eintraege || eintraege.length === 0) && !loading && (
            <tr><td colSpan={nCols} className="tbl-empty">Keine Einträge</td></tr>
          )}
          {gruppen.map((g) => {
            const e = g.main;
            const isWE = e.art === "Wochenende" || e.art === "Feiertag";
            const rels = e.releases || [];
            const zusatzRel = e.zusatz || [];
            // "extra" = weitere Einträge am selben Tag + Release-Zusatz
            const extraAlle = [...g.extra, ...zusatzRel];
            const hasExtra = extraAlle.length > 0;
            const isExpanded = expandedDays && expandedDays.has(e.datum);
            const isSource = copyMode && copyMode.id === e.id;
            const isCopyTarget = copyMode && !isWE && !isSource;
            const applied = copyApplied && copyApplied.has(e.datum);
            // Vorschau: angehakter Zieltag (Modus „füllen") zeigt schon die
            // Werte der Quelle — nur im Frontend, gespeichert erst bei „Übernehmen".
            const previewFill = applied && copyMode && copyModus === "fuellen";
            const disp = previewFill ? copyMode : e;
            const badge = ART_BADGE[disp.art] || { cls: "art-default", label: disp.art };
            const renderActions = (entry, isMain) => {
              // Ist DIESER Eintrag die Kopier-Quelle? → nur "Fertig" zeigen.
              if (copyMode && copyMode.id === entry.id) {
                const anz = copyApplied ? copyApplied.size : 0;
                return (
                  <td className="td-edit">
                    <button className="anz-edit-btn anz-copy-done"
                            disabled={copySaving || anz === 0}
                            onClick={onConfirmCopy}
                            title="Ausgewählte Zieltage füllen">
                      {copySaving ? "…" : `✓ Übernehmen (${anz})`}
                    </button>
                    <button className="anz-edit-btn anz-copy-cancel"
                            disabled={copySaving}
                            onClick={onExitCopy} title="Kopieren abbrechen">✕</button>
                  </td>
                );
              }
              return (
                <td className="td-edit">
                  <button className="anz-edit-btn" onClick={() => onEdit(entry)} title="Bearbeiten">✎</button>
                  {isMain && onAddTaetigkeit && !isWE && (
                    <button className="anz-edit-btn" onClick={() => onAddTaetigkeit(entry)}
                            title="Weitere Tätigkeit an diesem Tag (leer)">＋</button>
                  )}
                  {onKopieren && !isWE && !copyMode && (
                    <button className="anz-edit-btn" onClick={() => onKopieren(entry, !isMain)}
                            title={isMain
                              ? "Auf andere Tage kopieren (Tag füllen) – dann Zieltage anklicken"
                              : "Als weitere Tätigkeit auf andere Tage kopieren – dann Zieltage anklicken"}>⧉</button>
                  )}
                  {onDelete && !copyMode && (
                    <button className="anz-edit-btn anz-del-btn" onClick={() => onDelete(entry)}
                            title="Eintrag löschen">🗑</button>
                  )}
                </td>
              );
            };
            return (
              <React.Fragment key={e.id}>
                <tr className={(isWE ? "row-we " : "") + (isCopyTarget ? "row-copytarget " : "") + (isSource ? "row-copysource " : "") + (previewFill ? "row-copypreview" : "")}>
                  <td className="td-datum">
                    {isCopyTarget && (
                      <input type="checkbox" className="copy-check" checked={!!applied}
                             onChange={() => onCopyTarget(e.datum)}
                             title="Diesen Tag mit dem kopierten Eintrag füllen" />
                    )}
                    {hasExtra && toggleDayExpand ? (
                      <button className="anz-expand-btn" onClick={() => toggleDayExpand(e.datum)}>
                        {isExpanded ? "▼" : "▶"} {datumLabel(e.datum)}
                      </button>
                    ) : datumLabel(e.datum)}
                    {previewFill && <span className="anz-preview-tag" title="Wird beim Übernehmen gespeichert">Vorschau</span>}
                    {hasExtra && <span className="anz-extra-count" title={`${extraAlle.length} weitere Tätigkeit(en)`}>+{extraAlle.length}</span>}
                  </td>
                  <td><span className={`anz-art-badge ${badge.cls}`}>{badge.label}</span></td>
                  <td>{disp.arbeitsort || "–"}</td>
                  <td className="r mono">{fmtTime(disp.von)}</td>
                  <td className="r mono">{fmtTime(disp.bis)}</td>
                  <td className="r mono">{fmtPause(disp.pause)}</td>
                  <td className="r mono">{stdAnzeige(disp)}</td>
                  <td className="td-was">
                    <span className="was-text">{disp.was || "–"}</span>
                    {rels.length > 0 && onReleaseClick && (
                      <button className="anz-release-badge"
                        onClick={() => onReleaseClick({ datum: e.datum, releases: rels })}>
                        🚀 {rels.map(r => `v${r.version}`).join(", ")}
                      </button>
                    )}
                  </td>
                  <td className="td-epic"><span className="epic-text">{taetOf(disp) || "–"}</span></td>
                  <td className="td-epic"><span className="epic-text">{epicOf(disp) || "–"}</span></td>
                  {onEdit && renderActions(e, true)}
                </tr>
                {isExpanded && g.extra.map((z) => {
                  const zb = ART_BADGE[z.art] || { cls: "art-default", label: z.art };
                  return (
                    <tr key={z.id} className="row-zusatz">
                      <td className="td-datum"><span className="anz-sub-indent">└ weitere Tätigkeit</span></td>
                      <td><span className={`anz-art-badge ${zb.cls}`}>{zb.label}</span></td>
                      <td>{z.arbeitsort || "–"}</td>
                      <td className="r mono">{fmtTime(z.von)}</td>
                      <td className="r mono">{fmtTime(z.bis)}</td>
                      <td className="r mono">{fmtPause(z.pause)}</td>
                      <td className="r mono">{stdAnzeige(z)}</td>
                      <td className="td-was"><span className="was-text">{z.was || "–"}</span></td>
                      <td className="td-epic"><span className="epic-text">{taetOf(z) || "–"}</span></td>
                      <td className="td-epic"><span className="epic-text">{epicOf(z) || "–"}</span></td>
                      {onEdit && renderActions(z, false)}
                    </tr>
                  );
                })}
                {isExpanded && zusatzRel.map((z) => (
                  <tr key={z.id} className="row-zusatz">
                    <td className="td-datum"><span className="anz-sub-indent">└ Zusatz</span></td>
                    <td><span className="anz-art-badge art-zusatz">Zusatz</span></td>
                    <td>{z.arbeitsort || "–"}</td>
                    <td className="r mono">{fmtTime(z.von)}</td>
                    <td className="r mono">{fmtTime(z.bis)}</td>
                    <td className="r mono">{fmtPause(z.pause)}</td>
                    <td className="r mono">{z.arbeitszeit_h ? `${z.arbeitszeit_h} h` : "–"}</td>
                    <td className="td-was"><span className="was-text">{z.was || "–"}</span></td>
                    <td className="td-epic"><span className="epic-text">{taetOf(z) || "–"}</span></td>
                    <td className="td-epic"><span className="epic-text">{epicOf(z) || "–"}</span></td>
                    {onEdit && (
                      <td className="td-edit"><button className="anz-edit-btn" onClick={() => onEdit(z)}>✎</button></td>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/**
 * Admin-Detail-Ansicht für einen einzelnen Mitarbeiter (Drill-Down).
 * Zeigt Stammdaten · Saldo · WeekGrid · Anträge. Inline-Replace statt
 * neue Route — der Admin-Tab bleibt sichtbar.
 */
function AdminMaDetail({ ma, jahr, monat, setJahr, setMonat, saldo, uebersicht,
                          eintraege, antraege, loading, onBack, onEdit,
                          onEntscheiden, detailView, setDetailView, onExport }) {
  if (!ma) {
    return (
      <div className="admin-section">
        <p className="anz-loading">Lade Mitarbeiter…</p>
        <button className="anz-btn" onClick={onBack}>← Zurück zur Liste</button>
      </div>
    );
  }
  const monthOptions = [["", "Ganzes Jahr"]].concat(
    MONTHS_DE.map((m, i) => [String(i + 1), m]),
  );
  return (
    <div className="admin-section">
      <div className="admin-section-header" style={{flexWrap:"wrap", gap:10}}>
        <button className="anz-btn secondary" onClick={onBack}>← Zurück</button>
        <h3 className="anz-section-title" style={{margin:0, flex:1}}>
          <strong>{ma.kuerzel}</strong>
          <span style={{marginLeft:8, color:"var(--color-gray-500)", fontWeight:400}}>
            · {ma.vorname} {ma.nachname}
          </span>
          <span className={`admin-rolle-badge admin-rolle-${ma.rolle}`} style={{marginLeft:8}}>
            {ma.rolle}
          </span>
          <span className={`admin-zugang-badge admin-zugang-${ma.zugang_typ || "vollzugang"}`} style={{marginLeft:6}}>
            {ma.zugang_typ === "nur_zeiterfassung" ? "Zeit-App" : "Vollzugang"}
          </span>
          <span style={{marginLeft:10, color:"var(--color-gray-500)", fontSize:13}}
                title={BUNDESLAENDER[ma.bundesland] || ""}>
            {BUNDESLAENDER[ma.bundesland] || ma.bundesland || "–"}
          </span>
        </h3>
        <button className="anz-btn anz-btn-export" onClick={onExport}>⬇ Export</button>
        <button className="anz-btn" onClick={onEdit}>✎ Stammdaten</button>
      </div>

      {/* Saldo-Block */}
      {saldo && (
        <div className="ma-detail-saldo">
          <div className="ma-detail-saldo-row">
            <div className="ma-detail-saldo-pill">
              <span className="ma-detail-saldo-label">Saldo gesamt</span>
              <span className={`ma-detail-saldo-value ${(saldo.saldo_gesamt ?? saldo.saldo_h) >= 0 ? "pos" : "neg"}`}>
                {(saldo.saldo_gesamt ?? saldo.saldo_h) >= 0 ? "+" : ""}{(saldo.saldo_gesamt ?? saldo.saldo_h).toFixed(1)} h
              </span>
            </div>
            <div className="ma-detail-saldo-pill">
              <span className="ma-detail-saldo-label">Jahr {saldo.jahr} · Übertrag</span>
              <span className="ma-detail-saldo-value">
                {saldo.saldo_h >= 0 ? "+" : ""}{saldo.saldo_h.toFixed(1)} · {(saldo.vorjahres_saldo ?? 0).toFixed(1)} h
              </span>
            </div>
            <div className="ma-detail-saldo-pill">
              <span className="ma-detail-saldo-label">Soll · Ist</span>
              <span className="ma-detail-saldo-value">
                {saldo.soll_h.toFixed(0)} · {saldo.ist_h.toFixed(0)} h
              </span>
            </div>
            <div className="ma-detail-saldo-pill">
              <span className="ma-detail-saldo-label">Urlaubs-Saldo</span>
              <span className="ma-detail-saldo-value">
                {saldo.saldo_urlaub ?? saldo.urlaub_rest} Tage
              </span>
            </div>
            <div className="ma-detail-saldo-pill">
              <span className="ma-detail-saldo-label">Tagessoll (Mo–Fr)</span>
              <span className="ma-detail-saldo-value">
                {saldo.tagessoll_h ?? saldo.regelarbeitszeit_h} h/Tag · {saldo.arbeitstage_woche} Tage/Woche
              </span>
            </div>
          </div>
          <div className="ma-detail-saldo-erklaerung">{saldo.erklaerung}</div>
        </div>
      )}

      {/* Laufende Monats-Rechnung (Chef-Modell) */}
      {uebersicht && (uebersicht.monate || []).length > 0 && (
        <div className="admin-table-wrap" style={{marginTop:14}}>
          <table className="admin-table anz-monats-rechnung">
            <thead>
              <tr>
                <th>Monat</th>
                <th className="num" title="Erbrachte Stunden inkl. Sa/So">Erb. Std</th>
                <th className="num" title="Feiertage Mo–Fr (Kalender)">FT</th>
                <th className="num" title="Krank-Tage Mo–Fr">Krank</th>
                <th className="num" title="Urlaub genommen Mo–Fr">Url.</th>
                <th className="num" title="(FT + Krank + Urlaub) × Tagessoll">U/F/K Std</th>
                <th className="num" title="Erbracht + Gutschrift">Σ Std</th>
                <th className="num" title="Werktage − Feiertage">Soll-AT</th>
                <th className="num">Soll Std</th>
                <th className="num" title="Summe − Soll">Diff.</th>
                <th className="num">Saldo Std</th>
                <th className="num">Saldo Url.</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{opacity:0.85}}>
                <td><em>Vorjahr</em></td>
                <td className="num" colSpan={9}></td>
                <td className="num"><strong>{(uebersicht.vorjahr?.saldo_h ?? 0).toFixed(1)}</strong></td>
                <td className="num"><strong>{(uebersicht.vorjahr?.saldo_urlaub ?? 0).toFixed(1)}</strong></td>
              </tr>
              {uebersicht.monate.map((m) => (
                <tr key={m.monat}>
                  <td>{MONTHS_DE[m.monat - 1]}</td>
                  <td className="num">{(m.erbracht_h ?? 0).toFixed(2)}</td>
                  <td className="num">{m.feiertage ?? 0}</td>
                  <td className="num">{m.krank ?? 0}</td>
                  <td className="num">{m.urlaub ?? 0}</td>
                  <td className="num">{(m.gutschrift_h ?? 0).toFixed(1)}</td>
                  <td className="num">{(m.summe_h ?? 0).toFixed(2)}</td>
                  <td className="num">{m.soll_arbeitstage ?? 0}</td>
                  <td className="num">{(m.soll_h ?? 0).toFixed(1)}</td>
                  <td className="num"><span className={(m.diff_h ?? 0) >= 0 ? "b-pos" : "b-neg"}>{(m.diff_h ?? 0) >= 0 ? "+" : ""}{(m.diff_h ?? 0).toFixed(2)}</span></td>
                  <td className="num">{m.saldo_h != null ? (<strong className={m.saldo_h >= 0 ? "b-pos" : "b-neg"}>{m.saldo_h.toFixed(2)}</strong>) : "–"}</td>
                  <td className="num">{(m.saldo_urlaub ?? 0).toFixed(1)}</td>
                </tr>
              ))}
              <tr className="anz-footer-row">
                <td><strong>Gesamt</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.erbracht_h ?? 0),0).toFixed(2)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.feiertage ?? 0),0)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.krank ?? 0),0)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.urlaub ?? 0),0)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.gutschrift_h ?? 0),0).toFixed(1)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.summe_h ?? 0),0).toFixed(2)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.soll_arbeitstage ?? 0),0)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.soll_h ?? 0),0).toFixed(1)}</strong></td>
                <td className="num"><strong>{uebersicht.monate.reduce((s,m)=>s+(m.diff_h ?? 0),0).toFixed(2)}</strong></td>
                <td className="num"><strong>{(uebersicht.saldo_gesamt ?? 0).toFixed(2)}</strong></td>
                <td className="num"><strong>{(uebersicht.saldo_urlaub ?? 0).toFixed(1)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Filter-Reihe + Ansicht-Toggle */}
      <div className="admin-section-header" style={{flexWrap:"wrap", gap:8, marginTop:18}}>
        <h4 className="admin-konfig-title" style={{margin:0}}>Einträge</h4>
        <select className="anz-select" value={jahr}
          onChange={(e) => setJahr(+e.target.value)}>
          {[2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="anz-select" value={monat || ""}
          onChange={(e) => setMonat(e.target.value ? +e.target.value : null)}>
          {monthOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="admin-konfig-tabs" style={{margin:0, padding:2, marginLeft:"auto"}}>
          {[["liste","📋 Liste"], ["woche","🗓 Woche"]].map(([id, label]) => (
            <button key={id}
              className={`admin-konfig-tab ${(detailView || "liste") === id ? "active" : ""}`}
              onClick={() => setDetailView && setDetailView(id)}>{label}</button>
          ))}
        </div>
      </div>

      {!loading && eintraege?.length > 0 && (
        <TagesSumme eintraege={eintraege} regelarbeitszeit_h={saldo?.regelarbeitszeit_h} />
      )}
      {loading ? <p className="anz-loading">Lade…</p>
        : (detailView || "liste") === "liste"
          ? <TagesTable eintraege={eintraege} loading={loading}
              expandedDays={new Set()} toggleDayExpand={null} onEdit={null} onReleaseClick={null}
              regelarbeitszeit_h={saldo?.regelarbeitszeit_h} />
          : <WeekGrid eintraege={eintraege} onEdit={() => {/* Read-only */}} />}

      {/* Anträge dieses MAs */}
      <div className="admin-section-header" style={{flexWrap:"wrap", gap:8, marginTop:18}}>
        <h4 className="admin-konfig-title" style={{margin:0}}>
          Anträge <span className="admin-count-badge">{antraege.length}</span>
        </h4>
      </div>
      {antraege.length === 0 && <p className="tbl-empty">Keine Anträge</p>}
      {antraege.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Art</th><th>Von</th><th>Bis</th>
                  <th className="r">WT</th><th>Status</th>
                  <th>Anmerkung</th><th>Aktion</th></tr>
            </thead>
            <tbody>
              {antraege.map((a) => (
                <tr key={a.id}>
                  <td><span className={`badge badge-antrag-${a.art.toLowerCase()}`}>{a.art}</span></td>
                  <td>{a.von}</td>
                  <td>{a.bis}</td>
                  <td className="r">{a.anzahl_tage}</td>
                  <td><span className={`badge badge-antrag-status-${a.status}`}>{a.status}</span></td>
                  <td style={{maxWidth:240}}>{a.anmerkung || "–"}</td>
                  <td>
                    {a.status === "offen" ? (
                      <div style={{display:"flex", gap:6}}>
                        <button className="anz-btn anz-btn-sm anz-btn-save"
                          onClick={() => onEntscheiden(a.id, "genehmigen")}>✓</button>
                        <button className="anz-btn anz-btn-sm admin-bulk-btn-danger"
                          onClick={() => onEntscheiden(a.id, "ablehnen")}>✗</button>
                      </div>
                    ) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/**
 * Wochen-Grid: gruppiert die Einträge nach ISO-Woche und rendert eine
 * 7-Spalten-Übersicht Mo-So. Eintrag-Klick öffnet das Edit-Modal.
 */
function WeekGrid({ eintraege, onEdit }) {
  // Gruppiere nach ISO-Woche
  const weeks = useMemo(() => {
    const byKey = new Map();
    for (const e of eintraege || []) {
      if (!e.datum || e.art === "Release") continue;
      const dt = new Date(e.datum + "T00:00:00");
      const [yr, wk] = getISOWeek(dt);
      const key = `${yr}-W${String(wk).padStart(2, "0")}`;
      if (!byKey.has(key)) {
        byKey.set(key, { year: yr, week: wk, byDay: {} });
      }
      const grp = byKey.get(key);
      const dayKey = e.datum;
      if (!grp.byDay[dayKey]) grp.byDay[dayKey] = [];
      grp.byDay[dayKey].push(e);
    }
    return [...byKey.values()].sort((a, b) => b.year - a.year || b.week - a.week);
  }, [eintraege]);

  const dayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  return (
    <div className="week-grid-wrap">
      {weeks.length === 0 && <p className="tbl-empty">Keine Einträge</p>}
      {weeks.map((w) => {
        const monday = isoWeekMonday(w.year, w.week);
        const days = Array.from({ length: 7 }, (_, i) => {
          const dt = new Date(monday);
          dt.setUTCDate(monday.getUTCDate() + i);
          return dt.toISOString().slice(0, 10);
        });
        const totalH = days.reduce((sum, d) => {
          const items = w.byDay[d] || [];
          for (const it of items) {
            if (it.art === "Arbeit" || it.art === "Ist" || it.art === "Plan") {
              sum += parseFloat(it.arbeitszeit_h || 0);
            }
          }
          return sum;
        }, 0);
        return (
          <div key={`${w.year}-${w.week}`} className="week-grid-week">
            <div className="week-grid-header">
              <strong>KW {w.week}</strong>
              <span className="week-grid-meta">
                {monday.getUTCDate()}.{monday.getUTCMonth() + 1}.{w.year} –{" "}
                {(() => { const d = new Date(monday); d.setUTCDate(monday.getUTCDate() + 6); return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`; })()}
              </span>
              <span className="week-grid-total">{totalH.toFixed(1)} h</span>
            </div>
            <div className="week-grid-days">
              {days.map((dKey, i) => {
                const items = w.byDay[dKey] || [];
                const primary = items.find((x) => x.art !== "Zusatz") || items[0];
                const isWE = i >= 5;
                const isFeiertag = primary?.art === "Feiertag";
                return (
                  <div
                    key={dKey}
                    className={`week-grid-day ${isWE ? "is-we" : ""} ${isFeiertag ? "is-feiertag" : ""} ${primary?.art ? `art-${primary.art.toLowerCase()}` : ""}`}
                    onClick={() => primary && onEdit && onEdit(primary)}
                  >
                    <div className="week-grid-day-header">
                      <span className="week-grid-day-label">{dayLabels[i]}</span>
                      <span className="week-grid-day-num">{dKey.slice(8)}</span>
                    </div>
                    {primary ? (
                      <>
                        <div className="week-grid-day-art">
                          <span className={`badge ${ART_BADGE[primary.art]?.cls || ""}`}>
                            {ART_BADGE[primary.art]?.label || primary.art}
                          </span>
                        </div>
                        {(primary.art === "Arbeit" || primary.art === "Ist" || primary.art === "Plan") && (
                          <div className="week-grid-day-time">
                            {primary.von?.slice(0, 5) || "–"} – {primary.bis?.slice(0, 5) || "–"}
                            {primary.arbeitszeit_h && (
                              <span className="week-grid-day-h">{parseFloat(primary.arbeitszeit_h).toFixed(1)}h</span>
                            )}
                          </div>
                        )}
                        {primary.arbeitsort && <div className="week-grid-day-loc">{primary.arbeitsort}</div>}
                        {primary.was && <div className="week-grid-day-was">{primary.was}</div>}
                        {items.length > 1 && (
                          <div className="week-grid-day-extra">+{items.length - 1} weiterer</div>
                        )}
                      </>
                    ) : (
                      <div className="week-grid-day-empty">–</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


/**
 * Box, in der der Mitarbeiter seine Kalender-Abo-URL kopieren kann.
 * Webcal-Variante (öffnet System-Kalender-App direkt) + Copy-Button.
 */
function CalendarSubscribeBox({ token }) {
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  if (!token) return null;
  const httpsUrl = `${API_BASE}/arbeitsnachweis/calendar.ics?token=${encodeURIComponent(token)}`;
  const webcal = httpsUrl.replace(/^https?:\/\//, "webcal://");
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Diese URL kopieren:", httpsUrl);
    }
  };
  return (
    <div className="cal-subscribe-box">
      <div className="cal-subscribe-header">
        <div>
          <div className="cal-subscribe-title">📅 Kalender abonnieren</div>
          <div className="cal-subscribe-sub">
            Arbeitstage, Krank/Urlaub, Feiertage und Releases in deinen Kalender.
            Token-gesicherte URL — nur du hast Zugriff.
          </div>
        </div>
        <button type="button" className="cal-subscribe-help-toggle"
          onClick={() => setShowHelp((v) => !v)}>
          {showHelp ? "weniger" : "Hilfe"}
        </button>
      </div>
      <div className="cal-subscribe-actions">
        <a className="anz-btn" href={webcal} title="Öffnet die System-Kalender-App">📥 In Kalender öffnen</a>
        <button type="button" className="anz-btn anz-btn-export" onClick={handleCopy}>
          {copied ? "✓ kopiert" : "🔗 URL kopieren"}
        </button>
      </div>
      {showHelp && (
        <div className="cal-subscribe-help">
          <p><strong>iPhone / Mac:</strong> URL kopieren → Kalender-App → Datei → Neues Kalender-Abonnement → URL einfügen.</p>
          <p><strong>Google Kalender:</strong> Andere Kalender → + → Per URL hinzufügen → URL einfügen.</p>
          <p><strong>Outlook:</strong> Kalender hinzufügen → Aus dem Internet → URL einfügen.</p>
          <p className="cal-subscribe-warn">⚠ Die URL enthält dein Login-Token. Behandle sie wie ein Passwort.</p>
        </div>
      )}
    </div>
  );
}


const api = (path, opts = {}) => {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...opts.headers };
  if (token) headers["X-MA-Token"] = token;
  return fetch(API_BASE + "/arbeitsnachweis" + path, { ...opts, headers }).then(async (r) => {
    if (!r.ok) throw Object.assign(new Error("API " + r.status), { status: r.status });
    return r.json();
  });
};

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

const minToHHMM = (min) => {
  if (min === null || min === undefined) return "–";
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}:${String(m).padStart(2,"0")}`;
};

const timeStrToMin = (s) => {
  if (!s) return null;
  const parts = s.split(":");
  return parseInt(parts[0]) * 60 + parseInt(parts[1]);
};

const fmtTime = (t) => {
  if (!t) return "–";
  const parts = t.split(":");
  return `${String(parseInt(parts[0])).padStart(2,"0")}:${parts[1]}`;
};

const calcH = (von, bis, pauseMin) => {
  const v = timeStrToMin(von), b = timeStrToMin(bis);
  if (v === null || b === null) return null;
  return Math.max(0, (b - v - (pauseMin || 0)) / 60);
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export default function ArbeitsnachweisPa({ adminOnly = false }) {
  const navigate = useNavigate();
  const { token: ctxToken, logout: ctxLogout, authReady } = useAuth();

  // ── Auth ──
  const [maToken, setMaToken]   = useState(() => ctxToken || getToken());
  const [maInfo, setMaInfo]     = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Sync: wenn AuthContext-Token sich ändert, übernehmen
  useEffect(() => {
    if (ctxToken && ctxToken !== maToken) {
      setMaToken(ctxToken);
    }
  }, [ctxToken]); // eslint-disable-line

  useEffect(() => {
    const tok = ctxToken || getToken();
    if (!tok) { setAuthChecked(true); return; }
    setMaToken(tok);
    fetch(API_BASE + "/arbeitsnachweis/me", {
      headers: { "X-MA-Token": tok },
    }).then(async (r) => {
      if (r.ok) { const d = await r.json(); setMaInfo(d); }
      else { setToken(null); setMaToken(null); }
    }).catch(() => { setToken(null); setMaToken(null); })
      .finally(() => setAuthChecked(true));
  }, [maToken, ctxToken]); // eslint-disable-line

  const handleLogout = async () => {
    try { await api("/logout", { method: "POST" }); } catch {}
    setToken(null); setMaToken(null); setMaInfo(null);
    if (ctxLogout) ctxLogout();
  };

  // ── Navigation ──
  const istNurAdmin = adminOnly || maInfo?.zeigt_zeiterfassung === 0;
  const [tab, setTab] = useState(adminOnly ? "admin" : "zeiterfassung");

  // Wenn nur Admin → sofort auf Admin-Tab wechseln
  useEffect(() => {
    if (adminOnly) { setTab("admin"); return; }
    if (maInfo?.zeigt_zeiterfassung === 0) setTab("admin");
  }, [maInfo, adminOnly]);
  const [jahr, setJahr] = useState(currentYear);
  const [monat, setMonat] = useState(currentMonth);

  // ── CSV-Export Dialog ──
  const [exportDialog, setExportDialog] = useState(false);
  const [expModus,     setExpModus]     = useState("monat"); // "monat"|"jahr"|"alles"
  const [expJahr,      setExpJahr]      = useState(currentYear);
  const [expMonat,     setExpMonat]     = useState(currentMonth);
  const [expZusatz,    setExpZusatz]    = useState(true);

  // ── Live-Uhr ──
  const [uhr, setUhr] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setUhr(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Zeiterfassung State ──
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [selectedDatum, setSelectedDatum] = useState(todayISO());
  const datepickerRef = useRef(null);
  const [zeitVon, setZeitVon]           = useState("");
  const [zeitBis, setZeitBis]           = useState("");
  const [pauseMin, setPauseMin]         = useState(0);
  const [pauseActive, setPauseActive]   = useState(false);
  const pauseStartRef                   = useRef(null);
  const pauseAccRef                     = useRef(0);
  const [artHeute, setArtHeute]         = useState("Arbeit");
  const [ortHeute, setOrtHeute]         = useState("Homeoffice");
  const [wasHeute, setWasHeute]         = useState("");
  const [projektHeute, setProjektHeute] = useState("");
  const [bereicheHeute, setBereicheHeute] = useState([]);  // Array von bereich_label
  const [extraFelder, setExtraFelder]   = useState({});    // {feld_name: value}
  const [heuteReleases, setHeuteReleases] = useState([]);
  const [heuteDefault, setHeuteDefault] = useState(null);  // Vorlagen-Pre-Fill
  const [zeitSaving, setZeitSaving]     = useState(false);
  const [zeitLoaded, setZeitLoaded]     = useState(false);
  const autoSaveTimerRef                = useRef(null);
  // saveState: "idle" | "dirty" | "saving" | "saved" | "error"
  const [saveState, setSaveState]       = useState("idle");
  const [saveError, setSaveError]       = useState("");

  // Pausenminuten live hochzählen
  useEffect(() => {
    if (!pauseActive) return;
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - pauseStartRef.current) / 60000);
      setPauseMin(pauseAccRef.current + elapsed);
    }, 10000);
    return () => clearInterval(id);
  }, [pauseActive]);

  const loadHeute = useCallback(async (datum) => {
    try {
      const url = datum ? `/heute?datum=${datum}` : "/heute";
      const d = await api(url);
      setHeuteReleases(d.releases || []);
      // Pre-Fill nur anbieten, wenn Backend einen default geliefert hat
      // (= Tag ist leer UND es gibt eine aktive Vorlage für diesen Wochentag).
      setHeuteDefault(d.default || null);
      const e = d.eintrag;
      // Reset Felder
      setZeitVon(""); setZeitBis(""); setPauseMin(0); pauseAccRef.current = 0; setPauseActive(false);
      setArtHeute("Arbeit"); setWasHeute(""); setProjektHeute("");
      setBereicheHeute([]); setExtraFelder({});
      if (e) {
        if (e.von)    setZeitVon(e.von.substring(0,5));
        // bis nur laden wenn explizit gesetzt UND ungleich von (keine Auto-Befüllung)
        if (e.bis && e.bis !== e.von)  setZeitBis(e.bis.substring(0,5));
        if (e.pause) {
          const pm = timeStrToMin(e.pause.substring(0,5));
          setPauseMin(pm || 0);
          pauseAccRef.current = pm || 0;
        }
        if (e.art)        setArtHeute(e.art);
        if (e.arbeitsort) setOrtHeute(e.arbeitsort);
        else if (maInfo?.orte?.length > 0) setOrtHeute(maInfo.orte[0]);
        if (e.was)        setWasHeute(e.was);
        setProjektHeute(e.projekt_kuerzel || "");
        let parsedExtra = {};
        if (e.felder_json) {
          try {
            const fj = JSON.parse(e.felder_json);
            setBereicheHeute(fj.bereiche || []);
            parsedExtra = fj.extra || {};
          } catch {}
        }
        // epic aus DB-Spalte in extraFelder mergen (Rückwärtskompatibilität)
        if (e.epic && !parsedExtra.epic) parsedExtra = { ...parsedExtra, epic: e.epic };
        setExtraFelder(parsedExtra);
      }
      setZeitLoaded(true);
    } catch {}
  }, [maInfo]); // eslint-disable-line

  // Datum wechsel → Daten neu laden
  const handleDatumChange = (newDatum) => {
    setSelectedDatum(newDatum);
    setZeitLoaded(false);
  };

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "zeiterfassung" && !zeitLoaded) loadHeute(selectedDatum);
  }, [tab, zeitLoaded, loadHeute, authChecked, maToken, selectedDatum]);

  // maInfo neu laden wenn Zeiterfassung-Tab aktiv wird (dynamische Admin-Änderungen)
  const refreshMaInfo = useCallback(async () => {
    if (!maToken) return;
    try {
      const d = await api("/me");
      setMaInfo(d);
    } catch {}
  }, [maToken]); // eslint-disable-line

  useEffect(() => {
    if (tab === "zeiterfassung" && authChecked && maToken) refreshMaInfo();
  }, [tab]); // eslint-disable-line

  // ── Auto-Save: bei jeder Änderung mit 1.5s Debounce speichern ──
  // Nur speichern wenn zeitVon gesetzt ist (Arbeitstag wirklich gestartet).
  // Sichtbarer Status-Indikator (dirty/saving/saved/error) statt stilles
  // Verschlucken — wenn Speichern fehlschlägt, sieht der User es.
  const doAutoSave = useCallback(async (params) => {
    if (!maToken || !params.zeitLoaded || !params.zeitVon) return;
    setSaveState("dirty");
    setSaveError("");
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const felderJson = (params.bereicheHeute.length > 0 || Object.keys(params.extraFelder).length > 0)
          ? JSON.stringify({ bereiche: params.bereicheHeute, extra: params.extraFelder })
          : undefined;
        await api("/heute", {
          method: "POST",
          body: JSON.stringify({
            datum: params.selectedDatum,
            von: params.zeitVon,
            bis: params.zeitBis || undefined,
            pause_min: params.pauseMin,
            art: params.artHeute,
            arbeitsort: params.artHeute === "Arbeit" ? params.ortHeute : undefined,
            was: params.wasHeute || undefined,
            projekt_kuerzel: params.projektHeute || undefined,
            felder_json: felderJson,
          }),
        });
        setSaveState("saved");
        setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 2500);
      } catch (e) {
        setSaveState("error");
        setSaveError(e?.message || "Speichern fehlgeschlagen");
      }
    }, 1500);
  }, [maToken]); // eslint-disable-line

  useEffect(() => {
    if (!zeitLoaded) return;
    doAutoSave({ zeitLoaded, selectedDatum, zeitVon, zeitBis, pauseMin, artHeute, ortHeute, wasHeute, projektHeute, bereicheHeute, extraFelder });
  }, [zeitVon, zeitBis, pauseMin, artHeute, ortHeute, wasHeute, projektHeute, bereicheHeute, extraFelder]); // eslint-disable-line

  // ── Statistik / Übersicht ──
  const [jahresuebersicht, setJahresuebersicht] = useState([]);
  const [monate, setMonate]       = useState([]);
  const [monateMeta, setMonateMeta] = useState(null);
  const [releases, setReleases]   = useState([]);
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [toast, setToast]         = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());

  const loadStatistiken = useCallback(async () => {
    try {
      const j = await api("/statistik/jahresuebersicht");
      setJahresuebersicht(j.jahre || []);
    } catch (e) { setError(e.message); }
  }, []);

  const loadMonate = useCallback(async () => {
    try {
      const d = await api(`/statistik/monatsübersicht?jahr=${jahr}`);
      setMonate(d.monate || []);
      setMonateMeta({ vorjahr: d.vorjahr, saldo_gesamt: d.saldo_gesamt, saldo_urlaub: d.saldo_urlaub, tagessoll_h: d.tagessoll_h });
    } catch (e) { setError(e.message); }
  }, [jahr]);

  const loadReleases = useCallback(async () => {
    try {
      const d = await api("/releases?limit=300");
      setReleases(d.releases || []);
    } catch (e) { setError(e.message); }
  }, []);

  const [tageFilter, setTageFilter]   = useState("monat"); // "monat" | "jahr" | "alles"
  const [tageView, setTageView]       = useState("liste"); // "liste" | "woche"
  const [editModal, setEditModal]     = useState(null);    // { eintrag } | null
  const [editForm, setEditForm]       = useState({});
  const [editSaving, setEditSaving]   = useState(false);
  const [releaseDetail, setReleaseDetail] = useState(null); // { datum, releases: [...] } | null
  const [initMonatLoading, setInitMonatLoading] = useState(false);

  const loadEintraege = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/arbeitszeit?limit=1000";
      if (tageFilter !== "alles") url += `&jahr=${jahr}`;
      if (tageFilter === "monat") url += `&monat=${monat}`;
      const d = await api(url);
      setEintraege(d.eintraege || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [jahr, monat, tageFilter]);

  useEffect(() => { if (!authChecked || !maToken) return; loadStatistiken(); }, [loadStatistiken, authChecked, maToken]);
  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "statistik") loadMonate(); }, [tab, loadMonate, authChecked, maToken]);
  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "releases") loadReleases(); }, [tab, loadReleases, authChecked, maToken]);
  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "tage") loadEintraege(); }, [tab, loadEintraege, authChecked, maToken]);

  const ensureHeuteRanRef = useRef(false);

  // Beim Start: heute-Zeile automatisch anlegen wenn nicht vorhanden
  // useRef-Guard verhindert React-Strict-Mode Doppelaufruf
  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (ensureHeuteRanRef.current) return;
    ensureHeuteRanRef.current = true;
    api("/ensure-heute", { method: "POST" })
      .then(d => { if (d.created) loadEintraege(); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, maToken]);

  // ── Admin State ──
  const [adminSubTab, setAdminSubTab] = useState("mitarbeiter");
  const [adminMitarbeiter, setAdminMitarbeiter] = useState([]);
  const [adminMaLoading, setAdminMaLoading] = useState(false);
  const [adminMaModal, setAdminMaModal] = useState(null);
  const [adminMaForm, setAdminMaForm] = useState({});
  const [adminMaSaving, setAdminMaSaving] = useState(false);
  // ── Anträge (Urlaub/Krank) — Welle 5.3 ──
  const [antraege, setAntraege] = useState([]);
  const [antraegeAdmin, setAntraegeAdmin] = useState([]);
  const [antrAdminFilter, setAntrAdminFilter] = useState("offen"); // offen | alle | genehmigt | abgelehnt
  const [antrModal, setAntrModal] = useState(null); // null | {} | {edit}
  const [antrForm, setAntrForm] = useState({ art: "Urlaub", von: "", bis: "", anmerkung: "" });
  const [antrSaving, setAntrSaving] = useState(false);
  const antraegePendingCount = antraege.filter((a) => a.status === "offen").length;

  const loadAntraege = useCallback(async () => {
    try { const d = await api("/antraege"); setAntraege(d.antraege || []); }
    catch (e) { /* nur loggen */ }
  }, []);
  const loadAntraegeAdmin = useCallback(async () => {
    try {
      const p = antrAdminFilter === "alle" ? "" : `?status=${antrAdminFilter}`;
      const d = await api("/admin/antraege" + p);
      setAntraegeAdmin(d.antraege || []);
    } catch (e) {}
  }, [antrAdminFilter]);

  useEffect(() => { if (authChecked && maToken) loadAntraege(); }, [authChecked, maToken, loadAntraege]);
  useEffect(() => {
    if (tab === "admin" && adminSubTab === "antraege" && maInfo?.rolle === "admin") loadAntraegeAdmin();
  }, [tab, adminSubTab, maInfo, antrAdminFilter, loadAntraegeAdmin]);

  const submitAntrag = async () => {
    if (!antrForm.art || !antrForm.von || !antrForm.bis) {
      showToast("Bitte Art, Von und Bis ausfüllen", "error"); return;
    }
    setAntrSaving(true);
    try {
      await api("/antraege", { method: "POST", body: JSON.stringify(antrForm) });
      setAntrModal(null);
      setAntrForm({ art: "Urlaub", von: "", bis: "", anmerkung: "" });
      await loadAntraege();
      showToast("Antrag gestellt ✓");
    } catch (e) {
      showToast("Fehler: " + e.message, "error");
    } finally { setAntrSaving(false); }
  };
  const zurueckziehen = async (id) => {
    if (!window.confirm("Diesen Antrag wirklich zurückziehen?")) return;
    try {
      await api(`/antraege/${id}`, { method: "DELETE" });
      await loadAntraege();
      showToast("Zurückgezogen ✓");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
  };
  const adminEntscheiden = async (id, action) => {
    const verb = action === "genehmigen" ? "genehmigen" : "ablehnen";
    const kommentar = action === "ablehnen"
      ? window.prompt("Grund (optional):", "")
      : window.prompt("Kommentar (optional):", "");
    if (kommentar === null) return; // Cancel
    try {
      const d = await api(`/admin/antraege/${id}/${verb}`, {
        method: "PUT",
        body: JSON.stringify({ kommentar }),
      });
      await loadAntraegeAdmin();
      if (action === "genehmigen") {
        showToast(`Genehmigt ✓ (${d.eingetragene_tage} Tage eingetragen)`);
      } else {
        showToast("Abgelehnt");
      }
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
  };

  // Stundensaldo (Soll/Ist) kommt jetzt vom Backend (/statistik/saldo) —
  // berechnet mit Bundesland-Feiertagen und neutralem Krank.
  const [stundensaldo, setStundensaldo] = useState(null);
  useEffect(() => {
    if (!authChecked || !maToken) return;
    let cancelled = false;
    api(`/statistik/saldo?jahr=${currentYear}`)
      .then((d) => { if (!cancelled) setStundensaldo(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authChecked, maToken]);

  // Suche + Filter für die Mitarbeiter-Tabelle
  const [adminMaSearch, setAdminMaSearch] = useState("");
  const [adminMaFilter, setAdminMaFilter] = useState("aktiv"); // aktiv | inaktiv | alle
  // Drill-Down: wenn gesetzt, wird Detail-Ansicht statt Liste angezeigt.
  const [adminDetailMaId, setAdminDetailMaId] = useState(null);
  const [adminDetailJahr, setAdminDetailJahr] = useState(currentYear);
  const [adminDetailMonat, setAdminDetailMonat] = useState(null); // null = ganzes Jahr
  const [adminDetailView, setAdminDetailView] = useState("liste"); // "liste" | "woche"
  const [adminDetailSaldo, setAdminDetailSaldo] = useState(null);
  const [adminDetailUebersicht, setAdminDetailUebersicht] = useState(null);
  const [adminDetailEintraege, setAdminDetailEintraege] = useState([]);
  const [adminDetailAntraege, setAdminDetailAntraege] = useState([]);
  const [adminDetailLoading, setAdminDetailLoading] = useState(false);
  // Export-Dialog für EINE Person (Welle 23)
  const [maExportDlg, setMaExportDlg] = useState(null); // {doctyp, monat, firma, projekt, bestellnr} | null
  // Self-Export-Dialog (Welle 23): auch für Zeit-App-Nutzer
  const [selfExportDlg, setSelfExportDlg] = useState(null); // {doctyp, jahr, monat, firma, projekt, bestellnr} | null
  // Kopier-Modus (Welle 23): Quell-Eintrag wählen, dann Zieltage per Checkbox anklicken
  const [copyMode, setCopyMode] = useState(null);        // Quell-Eintrag | null
  const [copyModus, setCopyModus] = useState("fuellen"); // "fuellen" | "zusatz"
  const [copyApplied, setCopyApplied] = useState(new Set()); // ausgewählte Zieltage (nur Frontend)
  const [copySaving, setCopySaving] = useState(false);       // "Übernehmen" läuft
  const adminDetailMa = useMemo(
    () => adminMitarbeiter.find((m) => m.id === adminDetailMaId) || null,
    [adminMitarbeiter, adminDetailMaId],
  );

  const openMaExportDialog = () => {
    setMaExportDlg({
      doctyp: "vv",
      monat: adminDetailMonat || (new Date().getMonth() + 1),
      monat_von: 1, monat_bis: (new Date().getMonth() + 1), gesamt: false,
      firma: "", projekt: "", bestellnr: "",
    });
    // Vorbelegung aus dem letzten Export dieses Mitarbeiters (best-effort);
    // nur noch leere Felder werden befüllt
    if (adminDetailMaId) {
      api(`/admin/mitarbeiter/${adminDetailMaId}/export-defaults`).then((d) => {
        setMaExportDlg((prev) => prev && ({
          ...prev,
          firma: prev.firma || d.firma || "",
          projekt: prev.projekt || d.projekt || "",
          bestellnr: prev.bestellnr || d.bestellnr || "",
        }));
      }).catch(() => {});
    }
  };
  const doMaExport = () => {
    if (!adminDetailMaId || !maExportDlg) return;
    const p = new URLSearchParams();
    p.set("ma_id", adminDetailMaId);
    p.set("jahr", adminDetailJahr);
    const kz = (adminDetailMa?.kuerzel || "MA").toUpperCase();
    if (maExportDlg.doctyp === "vv") {
      p.set("monat", maExportDlg.monat);
      if (maExportDlg.firma) p.set("firma", maExportDlg.firma);
      if (maExportDlg.projekt) p.set("projekt", maExportDlg.projekt);
      if (maExportDlg.bestellnr) p.set("bestellnr", maExportDlg.bestellnr);
      downloadWithToken(`/admin/export/leistungsnachweis?${p.toString()}`,
        `${kz}_Leistungsnachweis_${adminDetailJahr}_${String(maExportDlg.monat).padStart(2, "0")}.xlsx`);
    } else if (maExportDlg.gesamt) {
      p.set("gesamt", "1");
      downloadWithToken(`/admin/export/monatsuebersicht?${p.toString()}`,
        `${kz}_Arbeitszeit_Gesamt.xlsx`);
    } else {
      const von = maExportDlg.monat_von || 1;
      const bis = maExportDlg.monat_bis || 12;
      p.set("monat_von", von);
      p.set("monat_bis", bis);
      downloadWithToken(`/admin/export/monatsuebersicht?${p.toString()}`,
        `${kz}_Arbeitszeit_${adminDetailJahr}_${String(von).padStart(2, "0")}-${String(bis).padStart(2, "0")}.xlsx`);
    }
    setMaExportDlg(null);
  };
  const loadAdminDetail = useCallback(async (maId, jahr, monat) => {
    if (!maId) return;
    setAdminDetailLoading(true);
    try {
      const monatPart = monat ? `&monat=${monat}` : "";
      const [s, e, a, u] = await Promise.all([
        api(`/admin/mitarbeiter/${maId}/saldo?jahr=${jahr}`),
        api(`/admin/mitarbeiter/${maId}/eintraege?jahr=${jahr}${monatPart}&limit=1500`),
        api(`/admin/antraege?ma_id=${maId}`),
        api(`/admin/mitarbeiter/${maId}/monatsuebersicht?jahr=${jahr}`).catch(() => null),
      ]);
      setAdminDetailSaldo(s);
      setAdminDetailEintraege(e.eintraege || []);
      setAdminDetailAntraege(a.antraege || []);
      setAdminDetailUebersicht(u);
    } catch (err) { /* nur loggen */ }
    finally { setAdminDetailLoading(false); }
  }, []);
  useEffect(() => {
    if (adminDetailMaId) loadAdminDetail(adminDetailMaId, adminDetailJahr, adminDetailMonat);
  }, [adminDetailMaId, adminDetailJahr, adminDetailMonat, loadAdminDetail]);
  // Konfigurations-Untersektion (Projekte | Felder | Bereiche | Orte | Vorlagen)
  const [adminKonfigSection, setAdminKonfigSection] = useState("projekte");
  // Wochentag-Defaults: nur State + Loader hier; die useEffect/Save-Handler
  // benutzen adminKonfigMaId und müssen daher NACH dessen Deklaration stehen.
  const [adminWotagDefaults, setAdminWotagDefaults] = useState([]);
  const [adminWotagSaving, setAdminWotagSaving] = useState(false);
  const loadAdminWotagDefaults = useCallback(async (maId) => {
    if (!maId) return;
    try {
      const d = await api(`/admin/mitarbeiter/${maId}/wochentag-defaults`);
      const byWotag = Object.fromEntries((d.defaults || []).map((x) => [x.wotag, x]));
      const labels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
      const filled = labels.map((label, i) => byWotag[i] || {
        wotag: i, wotag_label: label, art: "", von: "", bis: "",
        pause_min: null, arbeitsort: "", projekt_kuerzel: "", bereiche: [], aktiv: true,
      });
      setAdminWotagDefaults(filled);
    } catch (e) { /* silent */ }
  }, []);
  const adminMitarbeiterFiltered = useMemo(() => {
    const q = adminMaSearch.trim().toLowerCase();
    return adminMitarbeiter
      .filter((m) => {
        if (adminMaFilter === "aktiv"   && m.aktiv === false) return false;
        if (adminMaFilter === "inaktiv" && m.aktiv !== false) return false;
        if (!q) return true;
        const hay = `${m.kuerzel} ${m.vorname || ""} ${m.nachname || ""} ${m.email || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => (a.kuerzel || "").localeCompare(b.kuerzel || ""));
  }, [adminMitarbeiter, adminMaSearch, adminMaFilter]);
  const [adminKonfigMaId, setAdminKonfigMaId] = useState("");
  const [adminKonfig, setAdminKonfig] = useState({ projekte: [], felder: [], bereiche: [] });
  const [adminKonfigLoading, setAdminKonfigLoading] = useState(false);
  const [adminKonfigSaving, setAdminKonfigSaving] = useState(false);
  // useEffect + Save-Handler für Wochentag-Defaults: hierhin verschoben,
  // damit adminKonfigMaId schon initialisiert ist (Temporal Dead Zone-Fix).
  useEffect(() => {
    if (tab === "admin" && adminSubTab === "konfiguration"
        && adminKonfigSection === "vorlagen" && adminKonfigMaId) {
      loadAdminWotagDefaults(adminKonfigMaId);
    }
  }, [tab, adminSubTab, adminKonfigSection, adminKonfigMaId, loadAdminWotagDefaults]);
  const saveAdminWotagDefaults = async () => {
    if (!adminKonfigMaId) return;
    setAdminWotagSaving(true);
    try {
      await api(`/admin/mitarbeiter/${adminKonfigMaId}/wochentag-defaults`, {
        method: "PUT",
        body: JSON.stringify({ defaults: adminWotagDefaults }),
      });
      showToast("Vorlagen gespeichert ✓");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    finally { setAdminWotagSaving(false); }
  };
  const [adminShowNewProjekt, setAdminShowNewProjekt] = useState(false);
  const [adminNewProjekt, setAdminNewProjekt] = useState({ label: "", kuerzel: "", sort_order: 0 });
  const [adminShowNewFeld, setAdminShowNewFeld] = useState(false);
  const [adminNewFeld, setAdminNewFeld] = useState({ feld_name: "", feld_label: "", feld_typ: "text", optionen: "", sichtbar: true, pflichtfeld: false });
  const [adminEditFeldIdx, setAdminEditFeldIdx] = useState(null);
  const [adminEditProjektIdx, setAdminEditProjektIdx] = useState(null);
  const [adminEditBereichIdx, setAdminEditBereichIdx] = useState(null);
  const [adminShowNewBereich, setAdminShowNewBereich] = useState(false);
  const [adminNewBereich, setAdminNewBereich] = useState({ bereich_label: "", sichtbar: true });

  // Orte-State (Konfiguration-Tab)
  const [adminOrte, setAdminOrte] = useState([]);
  const [adminOrteSaving, setAdminOrteSaving] = useState(false);
  const [adminNewOrt, setAdminNewOrt] = useState("");

  const loadAdminOrte = useCallback(async (maId) => {
    if (!maId) return;
    try {
      const d = await api(`/admin/mitarbeiter/${maId}/orte`);
      setAdminOrte(d.orte || []);
    } catch { setAdminOrte([]); }
  }, [api]);

  const saveAdminOrte = async () => {
    setAdminOrteSaving(true);
    try {
      await api(`/admin/mitarbeiter/${adminKonfigMaId}/orte`, { method: "PUT", body: JSON.stringify(adminOrte) });
      showToast("Orte gespeichert", "success");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setAdminOrteSaving(false);
  };

  // ── Export-Spalten (pro MA): welche Spalten die Excel-Monatsmappe zeigt ──
  const EXPORT_SPALTEN_LABELS = {
    art: "Art", arbeitsort: "Ort", von: "Von", bis: "Bis", pause: "Pause",
    arbeitszeit_h: "Stunden (Std.)", zusatzfelder: "Zusatzfelder (z. B. Aufgaben-Spalten)",
    epic: "Epic/Thema", taetigkeit: "Tätigkeit", was: "Was (Beschreibung)",
  };
  const [adminExportSpalten, setAdminExportSpalten] = useState([]);
  const [adminExportSpaltenKeys, setAdminExportSpaltenKeys] = useState([]);
  const [adminExportSpaltenSaving, setAdminExportSpaltenSaving] = useState(false);
  const loadAdminExportSpalten = useCallback(async (maId) => {
    try {
      const d = await api(`/admin/mitarbeiter/${maId}/export-spalten`);
      setAdminExportSpaltenKeys(d.keys || []);
      setAdminExportSpalten(d.spalten || []);
    } catch { /* ignore */ }
  }, []);
  const saveAdminExportSpalten = async () => {
    if (!adminKonfigMaId) return;
    setAdminExportSpaltenSaving(true);
    try {
      await api(`/admin/mitarbeiter/${adminKonfigMaId}/export-spalten`, {
        method: "PUT", body: JSON.stringify({ spalten: adminExportSpalten }),
      });
      showToast("Export-Spalten gespeichert ✓");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setAdminExportSpaltenSaving(false);
  };

  // Export-Config State
  const [exportConfig, setExportConfig] = useState(null);
  const [exportConfigSaving, setExportConfigSaving] = useState(false);
  const EXPORT_FELDER_ALL = ["datum","art","arbeitsort","von","bis","arbeitszeit_h","pause","was","taetigkeit","projekt_kuerzel","epic"];

  const loadExportConfig = useCallback(async () => {
    try {
      const d = await api("/admin/export-config");
      setExportConfig(d);
    } catch { setExportConfig({ spalten: EXPORT_FELDER_ALL }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const saveExportConfig = async () => {
    setExportConfigSaving(true);
    try {
      await api("/admin/export-config", { method: "PUT", body: JSON.stringify({ spalten: exportConfig.spalten }) });
      showToast("Export-Konfiguration gespeichert", "success");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setExportConfigSaving(false);
  };

  // Import-Wizard State
  const [importStep, setImportStep] = useState(1); // 1=Upload, 2=Mapping, 3=Ergebnis
  const [importMaId, setImportMaId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // {spalten, vorschau, felder}
  const [importMapping, setImportMapping] = useState({}); // {colIdx: feldname}
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImportPreview = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const resp = await fetch(`http://localhost:8000/arbeitsnachweis/admin/import-preview`, {
        method: "POST", headers: { "x-ma-token": maToken }, body: fd,
      });
      if (!resp.ok) throw new Error(await resp.text());
      const d = await resp.json();
      setImportPreview(d);
      const autoMap = {};
      d.spalten.forEach((s, i) => {
        const normalized = s.toLowerCase().replace(/[^a-z_]/g, "");
        const match = d.felder.find(f => f === normalized || normalized.includes(f));
        if (match) autoMap[i] = match;
      });
      setImportMapping(autoMap);
      setImportStep(2);
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setImportLoading(false);
  };

  const handleImportExecute = async () => {
    if (!importFile || !importMaId) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("ma_id_param", importMaId);
      fd.append("mapping_json", JSON.stringify(Object.fromEntries(Object.entries(importMapping).filter(([,v])=>v))));
      const resp = await fetch(`http://localhost:8000/arbeitsnachweis/admin/import-execute`, {
        method: "POST", headers: { "x-ma-token": maToken }, body: fd,
      });
      if (!resp.ok) throw new Error(await resp.text());
      const d = await resp.json();
      setImportResult(d);
      setImportStep(3);
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setImportLoading(false);
  };

  // ── Admin Statistiken ──
  const [adminStatMaId, setAdminStatMaId] = useState("");
  const [adminStatJahr, setAdminStatJahr] = useState(currentYear);
  const [adminStatData, setAdminStatData] = useState([]);
  const [adminStatLoading, setAdminStatLoading] = useState(false);
  // Statistik-Ansicht: tabelle | heatmap
  const [adminStatView, setAdminStatView] = useState("tabelle");
  const [adminStatSearch, setAdminStatSearch] = useState("");
  // Summen-Berechnung + Soll-Diff (regelarbeitszeit_h * arbeitstage)
  const adminStatRows = useMemo(() => {
    const q = adminStatSearch.trim().toLowerCase();
    return (adminStatData || [])
      .filter((r) => {
        if (!q) return true;
        const hay = `${r.kuerzel || ""} ${r.vorname || ""} ${r.nachname || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .map((r) => ({
        ...r,
        // Soll/Diff kommen jetzt SERVERSEITIG aus dem Chef-Modell (Welle 34)
        soll: r.soll_h ?? 0,
        ist: r.stunden || 0,
        diff: r.diff_h ?? ((r.stunden || 0) - (r.soll_h || 0)),
        regel: r.tagessoll_h ?? 8,
      }))
      .sort((a, b) => (a.kuerzel || "").localeCompare(b.kuerzel || "") || (b.jahr - a.jahr));
  }, [adminStatData, adminStatSearch]);
  const adminStatTotals = useMemo(() => {
    const t = { stunden: 0, arbeitstage: 0, krank_tage: 0, urlaub_tage: 0, feiertage: 0, soll: 0, diff: 0 };
    for (const r of adminStatRows) {
      t.stunden += r.stunden || 0;
      t.arbeitstage += r.arbeitstage || 0;
      t.krank_tage += r.krank_tage || 0;
      t.urlaub_tage += r.urlaub_tage || 0;
      t.feiertage += r.feiertage || 0;
      t.soll += r.soll || 0;
      t.diff += r.diff || 0;
    }
    return t;
  }, [adminStatRows]);

  // ── Admin Modul-Freigaben ──
  const [adminModulMaId, setAdminModulMaId] = useState("");
  const [adminModulList, setAdminModulList] = useState([]); // aktive Module für selektierten MA
  const [adminModulSaving, setAdminModulSaving] = useState(false);
  const ALLE_MODULE = ['rename','rechnungen','monitoring','mietverwaltung','zuordnen','fahrtenbuch','zaehlerstand','ki','arbeitsnachweis','datenbank','mapping'];

  const loadAdminModule = useCallback(async (maId) => {
    if (!maId) return;
    try {
      const d = await api(`/admin/mitarbeiter/${maId}/module`);
      // Backend gibt Dict {modul: true/false} → in Array aktiver Module umwandeln
      const moduleDict = d.module || {};
      setAdminModulList(Object.keys(moduleDict).filter(k => moduleDict[k]));
    } catch {}
  }, []);

  const handleAdminModulSave = async () => {
    if (!adminModulMaId) return;
    setAdminModulSaving(true);
    try {
      // Array → Dict {modul: true/false} für Backend
      const moduleDict = Object.fromEntries(ALLE_MODULE.map(m => [m, adminModulList.includes(m)]));
      await api(`/admin/mitarbeiter/${adminModulMaId}/module`, {
        method: "PUT",
        body: JSON.stringify(moduleDict),
      });
      showToast("Module gespeichert ✓");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
    setAdminModulSaving(false);
  };

  const loadAdminStatistik = useCallback(async (maId, jahr_) => {
    setAdminStatLoading(true);
    try {
      let url = "/admin/statistik/uebersicht";
      const p = new URLSearchParams();
      if (maId)  p.set("ma_id", maId);
      if (jahr_) p.set("jahr", jahr_);
      if (p.toString()) url += "?" + p.toString();
      const d = await api(url);
      setAdminStatData(d.statistik || []);
    } catch (e) { setError(e.message); }
    finally { setAdminStatLoading(false); }
  }, []);

  const doAdminStatExport = () => {
    const p = new URLSearchParams();
    if (adminStatMaId) p.set("ma_id", adminStatMaId);
    if (adminStatJahr) p.set("jahr", adminStatJahr);
    downloadWithToken(`/admin/statistik/export?${p.toString()}`,
      `admin_arbeitsnachweis_${adminStatJahr || ""}.csv`);
  };

  const loadAdminMitarbeiter = useCallback(async () => {
    setAdminMaLoading(true);
    try {
      const d = await api("/admin/mitarbeiter");
      setAdminMitarbeiter(d.mitarbeiter || []);
    } catch (e) { setError(e.message); }
    finally { setAdminMaLoading(false); }
  }, []);

  const loadAdminKonfig = useCallback(async (maId) => {
    if (!maId) return;
    setAdminKonfigLoading(true);
    try {
      const d = await api(`/admin/mitarbeiter/${maId}/config`);
      setAdminKonfig({ projekte: d.projekte || [], felder: d.felder || [], bereiche: d.bereiche || [] });
    } catch (e) { setError(e.message); }
    finally { setAdminKonfigLoading(false); }
  }, []);

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "admin" && (adminSubTab === "mitarbeiter" || adminSubTab === "statistiken" || adminSubTab === "konfiguration" || adminSubTab === "module" || adminSubTab === "import")) {
      loadAdminMitarbeiter();
    }
    if (tab === "admin" && adminSubTab === "export") loadExportConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, adminSubTab, authChecked, maToken]);

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "admin" && adminSubTab === "statistiken") loadAdminStatistik(adminStatMaId, adminStatJahr);
  }, [tab, adminSubTab, adminStatMaId, adminStatJahr, loadAdminStatistik, authChecked, maToken]); // eslint-disable-line

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "admin" && adminSubTab === "konfiguration" && adminKonfigMaId) {
      loadAdminKonfig(adminKonfigMaId);
      loadAdminOrte(adminKonfigMaId);
      loadAdminExportSpalten(adminKonfigMaId);
    }
  }, [tab, adminSubTab, adminKonfigMaId, loadAdminKonfig, loadAdminOrte, loadAdminExportSpalten, authChecked, maToken]);

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "admin" && adminSubTab === "module" && adminModulMaId) {
      loadAdminModule(adminModulMaId);
    }
  }, [tab, adminSubTab, adminModulMaId, loadAdminModule, authChecked, maToken]);

  if (!authChecked) return <div className="anz-loading">Lade…</div>;

  if (!authReady || !authChecked) return (
    <div className="anz-login-wrap">
      <div className="anz-login-box" style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>
        Lade…
      </div>
    </div>
  );

  if (!maToken || !maInfo) {
    // Nicht eingeloggt → zur Hauptseite
    if (authChecked && !maToken) {
      navigate("/");
      return null;
    }
    return (
      <div className="anz-login-wrap">
        <div className="anz-login-box" style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>
          Weiterleitung…
        </div>
      </div>
    );
  }

  // ── CSV-Export Dialog ──

  const openExportDialog = () => {
    setExpModus("monat");
    setExpJahr(jahr);
    setExpMonat(monat);
    setExpZusatz(true);
    setExportDialog(true);
  };

  // Datei-Download mit Header-Token (statt window.open) — umgeht Popup-Blocker
  // und Token-in-URL-Probleme. Nutzt dieselbe Auth wie die normalen API-Calls.
  const downloadWithToken = async (path, filename) => {
    const token = maToken || ctxToken || getToken();
    try {
      const res = await fetch(`${API_BASE}/arbeitsnachweis${path}`, {
        headers: token ? { "X-MA-Token": token } : {},
      });
      if (!res.ok) {
        showToast(`Export fehlgeschlagen (HTTP ${res.status})`, "error");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) {
      showToast("Export fehlgeschlagen: " + e.message, "error");
    }
  };

  const doExport = () => {
    const p = new URLSearchParams();
    if (expModus === "monat" || expModus === "jahr") p.set("jahr", expJahr);
    if (expModus === "monat") p.set("monat", expMonat);
    p.set("mit_zusatz", expZusatz ? "true" : "false");
    downloadWithToken(`/export?${p.toString()}`,
      `arbeitsnachweis_${expJahr}${expModus === "monat" ? "_" + String(expMonat).padStart(2, "0") : ""}.csv`);
    setExportDialog(false);
  };

  // Self-Export (VV-Excel / CSV) — für eigenen Account, auch Zeit-App-Nutzer
  const openSelfExport = () => {
    setSelfExportDlg({
      doctyp: "vv", jahr: jahr || currentYear, monat: (new Date().getMonth() + 1),
      monat_von: 1, monat_bis: (new Date().getMonth() + 1), gesamt: false,
      firma: "", projekt: "", bestellnr: "",
    });
    // Vorbelegung aus dem letzten eigenen Export (best-effort)
    api("/export/defaults").then((d) => {
      setSelfExportDlg((prev) => prev && ({
        ...prev,
        firma: prev.firma || d.firma || "",
        projekt: prev.projekt || d.projekt || "",
        bestellnr: prev.bestellnr || d.bestellnr || "",
      }));
    }).catch(() => {});
  };
  const doSelfExport = () => {
    if (!selfExportDlg) return;
    const p = new URLSearchParams();
    p.set("jahr", selfExportDlg.jahr);
    if (selfExportDlg.doctyp === "vv") {
      p.set("monat", selfExportDlg.monat);
      if (selfExportDlg.firma) p.set("firma", selfExportDlg.firma);
      if (selfExportDlg.projekt) p.set("projekt", selfExportDlg.projekt);
      if (selfExportDlg.bestellnr) p.set("bestellnr", selfExportDlg.bestellnr);
      downloadWithToken(`/export/leistungsnachweis?${p.toString()}`,
        `Leistungsnachweis_${selfExportDlg.jahr}_${String(selfExportDlg.monat).padStart(2, "0")}.xlsx`);
    } else if (selfExportDlg.gesamt) {
      p.set("gesamt", "1");
      downloadWithToken(`/export/monatsuebersicht?${p.toString()}`,
        `Arbeitszeit_Gesamt.xlsx`);
    } else {
      const von = selfExportDlg.monat_von || 1;
      const bis = selfExportDlg.monat_bis || 12;
      p.set("monat_von", von);
      p.set("monat_bis", bis);
      downloadWithToken(`/export/monatsuebersicht?${p.toString()}`,
        `Arbeitszeit_${selfExportDlg.jahr}_${String(von).padStart(2, "0")}-${String(bis).padStart(2, "0")}.xlsx`);
    }
    setSelfExportDlg(null);
  };

  const handleStart = async () => {
    // Niemals eine bestehende Startzeit überschreiben
    if (zeitVon) return;
    const von = nowHHMM();
    setZeitVon(von);
    // Sofort in DB speichern
    try {
      await api("/heute", {
        method: "POST",
        body: JSON.stringify({ von, art: artHeute, datum: selectedDatum }),
      });
    } catch {}
  };
  const handleSchluss = () => {
    if (pauseActive) handlePauseToggle();
    setZeitBis(nowHHMM());
  };

  const handlePauseToggle = () => {
    if (!pauseActive) {
      pauseStartRef.current = Date.now();
      setPauseActive(true);
    } else {
      const elapsed = Math.floor((Date.now() - pauseStartRef.current) / 60000);
      pauseAccRef.current += elapsed;
      setPauseMin(pauseAccRef.current);
      setPauseActive(false);
    }
  };

  const handleSaveHeute = async () => {
    setZeitSaving(true);
    try {
      const felderJson = (bereicheHeute.length > 0 || Object.keys(extraFelder).length > 0)
        ? JSON.stringify({ bereiche: bereicheHeute, extra: extraFelder })
        : undefined;
      await api("/heute", {
        method: "POST",
        body: JSON.stringify({
          datum: selectedDatum,
          von: zeitVon || undefined,
          bis: zeitBis || undefined,
          pause_min: pauseMin,
          art: artHeute,
          arbeitsort: artHeute === "Arbeit" ? ortHeute : undefined,
          was: wasHeute,
          projekt_kuerzel: projektHeute || undefined,
          felder_json: felderJson,
        }),
      });
      showToast("Gespeichert ✓");
    } catch (e) {
      showToast("Fehler: " + e.message, "error");
    }
    setZeitSaving(false);
  };

  // ── Statistik / Übersicht ──

  const toggleDayExpand = (datum) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(datum)) next.delete(datum);
      else next.add(datum);
      return next;
    });
  };

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openEdit = (e) => {
    // Tätigkeit/Epic können in der DB-Spalte ODER in felder_json.extra liegen
    // (Zeiterfassung speichert sie in felder_json) — beides berücksichtigen.
    let extra = {};
    try {
      const fj = typeof e.felder_json === "string" ? JSON.parse(e.felder_json) : e.felder_json;
      extra = (fj && fj.extra) || {};
    } catch { /* ignore */ }
    setEditForm({
      art:        e.art || "Arbeit",
      arbeitsort: e.arbeitsort || "",
      von:        toHHMM(e.von),
      bis:        toHHMM(e.bis),
      pause_min:  e.pause ? timeStrToMin(e.pause.substring(0,5)) || 0 : 0,
      arbeitszeit_h: e.arbeitszeit_h || "",
      was:        e.was || "",
      taetigkeit: e.taetigkeit || extra.taetigkeit || "",
      epic:       e.epic || extra.epic || "",
    });
    setEditModal(e);
  };

  // ── Werte von einem früheren Tag übernehmen ────────────────────────────
  //
  // Zwei Fälle, die im Alltag ständig vorkommen: „wie gestern" und „wie am
  // selben Wochentag letzte Woche" (bei festen Wochenrhythmen). Übernommen
  // wird ALLES — Art, Ort, Zeiten, Pause, Tätigkeit, Epic und der Text.
  // Gespeichert wird nicht automatisch: Der Vorschlag steht im Formular und
  // wird mit „Speichern" übernommen, damit man vorher noch anpassen kann.

  /** Felder eines Eintrags in das Bearbeiten-Formular übertragen. */
  const _formAusEintrag = (e) => {
    let extra = {};
    try {
      const fj = typeof e.felder_json === "string" ? JSON.parse(e.felder_json) : e.felder_json;
      extra = (fj && fj.extra) || {};
    } catch { /* ignore */ }
    return {
      art:        e.art || "Arbeit",
      arbeitsort: e.arbeitsort || "",
      von:        toHHMM(e.von),
      bis:        toHHMM(e.bis),
      pause_min:  e.pause ? timeStrToMin(e.pause.substring(0, 5)) || 0 : 0,
      arbeitszeit_h: e.arbeitszeit_h || "",
      was:        e.was || "",
      taetigkeit: e.taetigkeit || extra.taetigkeit || "",
      epic:       e.epic || extra.epic || "",
    };
  };

  /**
   * Sucht die Vorlage rückwärts ab dem Bezugstag.
   *
   * tageZurueck = 1 → gestern, 7 → gleicher Wochentag letzte Woche.
   * Ist der Tag leer (Wochenende, Urlaub, gar kein Eintrag), wird bis zu
   * 14 Tage weiter zurückgegangen — sonst liefe „von gestern" montags immer
   * ins Leere, weil Sonntag nichts steht.
   */
  const vorlageSuchen = (bezugsDatum, tageZurueck) => {
    const istBrauchbar = (e) =>
      e && (e.art || "").toLowerCase() === "arbeit" && (e.von || e.arbeitszeit_h);
    const d = new Date(bezugsDatum + "T12:00:00");
    for (let i = 0; i < 14; i++) {
      const kandidat = new Date(d);
      kandidat.setDate(d.getDate() - tageZurueck - i);
      const iso = kandidat.toISOString().slice(0, 10);
      const treffer = eintraege.find(
        (e) => String(e.datum).slice(0, 10) === iso && istBrauchbar(e));
      if (treffer) return { eintrag: treffer, datum: iso };
      // Beim Wochen-Sprung in Wochenschritten weitersuchen, damit der
      // Wochentag erhalten bleibt (sonst wäre es nur „irgendein Tag davor").
      if (tageZurueck === 7) i += 6;
    }
    return null;
  };

  /**
   * Dasselbe für die Zeiterfassung (erste Seite).
   *
   * Dort liegen die Werte nicht in einem Formular-Objekt, sondern in einzelnen
   * Zuständen — deshalb eine eigene Funktion statt setEditForm. Der Auto-Save
   * greift danach von selbst, weil sich die Zustände ändern.
   */
  const zeitUebernehmenVon = (tageZurueck) => {
    const fund = vorlageSuchen(selectedDatum || todayISO(), tageZurueck);
    if (!fund) {
      showToast(tageZurueck === 1
        ? "Kein Arbeitstag in den letzten 14 Tagen gefunden"
        : "Kein passender Wochentag in den letzten Wochen gefunden");
      return;
    }
    const f = _formAusEintrag(fund.eintrag);
    setArtHeute(f.art);
    setOrtHeute(f.arbeitsort || ortHeute);
    setZeitVon(f.von);
    setZeitBis(f.bis);
    setPauseMin(f.pause_min || 0);
    pauseAccRef.current = f.pause_min || 0;
    setWasHeute(f.was);
    // Tätigkeit und Epic liegen in der Zeiterfassung unter den Zusatzfeldern
    setExtraFelder((vorher) => ({
      ...vorher,
      ...(f.taetigkeit ? { taetigkeit: f.taetigkeit } : {}),
      ...(f.epic ? { epic: f.epic } : {}),
    }));
    const wotag = new Date(fund.datum + "T12:00:00")
      .toLocaleDateString("de-DE", { weekday: "short" });
    showToast(`Übernommen von ${wotag}, ${fund.datum.split("-").reverse().join(".")}`);
  };

  const uebernehmenVon = (tageZurueck) => {
    if (!editModal) return;
    const basis = String(editModal.datum).slice(0, 10);
    const fund = vorlageSuchen(basis, tageZurueck);
    if (!fund) {
      showToast(tageZurueck === 1
        ? "Kein Arbeitstag in den letzten 14 Tagen gefunden"
        : "Kein passender Wochentag in den letzten Wochen gefunden");
      return;
    }
    setEditForm(_formAusEintrag(fund.eintrag));
    const wotag = new Date(fund.datum + "T12:00:00")
      .toLocaleDateString("de-DE", { weekday: "short" });
    showToast(`Übernommen von ${wotag}, ${fund.datum.split("-").reverse().join(".")}`);
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setEditSaving(true);
    try {
      await api(`/arbeitszeit/${editModal.id}`, {
        method: "PUT",
        body: JSON.stringify({
          art:          editForm.art,
          arbeitsort:   editForm.arbeitsort || null,
          von:          editForm.von || null,
          bis:          editForm.bis || null,
          pause_min:    editForm.pause_min ?? 0,
          arbeitszeit_h: editForm.von && editForm.bis ? undefined : (parseFloat(editForm.arbeitszeit_h) || null),
          was:          editForm.was || null,
          taetigkeit:   editForm.taetigkeit || null,
          epic:         editForm.epic || null,
        }),
      });
      setEditModal(null);
      showToast("Gespeichert ✓");
      loadEintraege();
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    }
    setEditSaving(false);
  };

  const handleEintragAnlegen = async (datumStr) => {
    try {
      await api(`/ensure-tag/${datumStr}`, { method: "POST" });
      showToast("Eintrag angelegt ✓");
      loadEintraege();
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    }
  };

  // Welle 23: weitere (leere) Tätigkeit am selben Tag anlegen
  const handleAddTaetigkeit = async (e) => {
    try {
      const res = await api("/arbeitszeit/neu", {
        method: "POST",
        body: JSON.stringify({ datum: e.datum, art: "Arbeit" }),
      });
      showToast("Weitere Tätigkeit angelegt ✓");
      await loadEintraege();
      if (res?.id) openEdit({ id: res.id, datum: e.datum, art: "Arbeit" });
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    }
  };
  // Kopier-Modus starten: Quelle merken; danach klickt man die Zieltage per Checkbox an.
  // isZusatz=true (weitere Tätigkeit) → Zieltag NICHT überschreiben, sondern zusätzlich anhängen.
  const startCopyMode = (e, isZusatz) => {
    setCopyMode(e);
    setCopyModus(isZusatz ? "zusatz" : "fuellen");
    setCopyApplied(new Set());
    showToast("Kopieren aktiv – Zieltage anhaken, dann „Übernehmen“");
  };
  // Abbrechen: Auswahl verwerfen, nichts speichern.
  const exitCopyMode = () => { setCopyMode(null); setCopyApplied(new Set()); };
  // Zieltag an-/abwählen — passiert NUR im Frontend (kein Speichern).
  const handleCopyTarget = (datum) => {
    if (!copyMode) return;
    setCopyApplied((prev) => {
      const n = new Set(prev);
      if (n.has(datum)) n.delete(datum); else n.add(datum);
      return n;
    });
  };
  // „Übernehmen“: alle ausgewählten Zieltage in EINEM Aufruf speichern.
  const confirmCopyMode = async () => {
    if (!copyMode) return;
    const ziele = [...copyApplied];
    if (ziele.length === 0) { exitCopyMode(); return; }
    setCopySaving(true);
    try {
      const r = await api(`/arbeitszeit/${copyMode.id}/kopieren-auf`, {
        method: "POST",
        body: JSON.stringify({ ziele, nur_werktage: false, modus: copyModus }),
      });
      const n = (r?.angelegt || 0) + (r?.ueberschrieben || 0);
      showToast(`${n} Tag(e) übernommen ✓`);
      setCopyMode(null);
      setCopyApplied(new Set());
      await loadEintraege();
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    } finally {
      setCopySaving(false);
    }
  };
  const handleDeleteEintrag = async (e) => {
    if (!window.confirm(`Eintrag vom ${e.datum} wirklich löschen?`)) return;
    try {
      await api(`/arbeitszeit/${e.id}`, { method: "DELETE" });
      showToast("Gelöscht ✓");
      loadEintraege();
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    }
  };

  const handleInitMonat = async () => {
    setInitMonatLoading(true);
    try {
      const d = await api(`/init-monat?jahr=${jahr}&monat=${monat}`, { method: "POST" });
      const feiertagsNames = Object.values(d.feiertage || {});
      let msg = `${d.angelegt} Tage angelegt`;
      if (feiertagsNames.length) msg += ` (Feiertage: ${feiertagsNames.join(", ")})`;
      showToast(msg);
      loadEintraege();
    } catch (err) {
      showToast("Fehler: " + err.message, "error");
    }
    setInitMonatLoading(false);
  };

  // ── Admin Funktionen ──
  const openAdminMaNew = () => {
    setAdminMaForm({ kuerzel: "", vorname: "", nachname: "", email: "", rolle: "mitarbeiter", bundesland: "HB", passwort: "", aktiv: true, zugang_typ: "vollzugang", zeigt_zeiterfassung: 1, regelarbeitszeit_h: 8, urlaubstage_pro_jahr: 30, arbeitstage_woche: 5, reisekosten_pauschale: 0, auftragsvolumen_pt: 0, plan_tage_monat: 0, vertragsdatum: "", wochensoll_h: 0, saldo_uebertrag_h: 0, urlaub_uebertrag_tage: 0, uebertrag_ab_jahr: 0 });
    setAdminMaModal({ mode: "new" });
  };

  const openAdminMaEdit = (ma) => {
    setAdminMaForm({ vorname: ma.vorname || "", nachname: ma.nachname || "", email: ma.email || "", rolle: ma.rolle || "mitarbeiter", bundesland: ma.bundesland || "HB", passwort: "", aktiv: ma.aktiv !== false, zugang_typ: ma.zugang_typ || "vollzugang", zeigt_zeiterfassung: ma.zeigt_zeiterfassung !== undefined ? ma.zeigt_zeiterfassung : 1, regelarbeitszeit_h: ma.regelarbeitszeit_h ?? 8, urlaubstage_pro_jahr: ma.urlaubstage_pro_jahr ?? 30, arbeitstage_woche: ma.arbeitstage_woche ?? 5, reisekosten_pauschale: parseFloat(ma.reisekosten_pauschale) || 0, auftragsvolumen_pt: parseFloat(ma.auftragsvolumen_pt) || 0, plan_tage_monat: parseFloat(ma.plan_tage_monat) || 0, vertragsdatum: (ma.vertragsdatum || "").slice(0, 10), wochensoll_h: parseFloat(ma.wochensoll_h) || 0, saldo_uebertrag_h: parseFloat(ma.saldo_uebertrag_h) || 0, urlaub_uebertrag_tage: parseFloat(ma.urlaub_uebertrag_tage) || 0, uebertrag_ab_jahr: parseInt(ma.uebertrag_ab_jahr) || 0 });
    setAdminMaModal({ mode: "edit", ma });
  };

  const handleAdminMaSave = async () => {
    setAdminMaSaving(true);
    try {
      if (adminMaModal.mode === "new") {
        const body = { ...adminMaForm, kuerzel: adminMaForm.kuerzel.toUpperCase() };
        await api("/admin/mitarbeiter", { method: "POST", body: JSON.stringify(body) });
      } else {
        const body = { ...adminMaForm };
        if (!body.passwort) delete body.passwort;
        delete body.kuerzel;
        await api(`/admin/mitarbeiter/${adminMaModal.ma.id}`, { method: "PUT", body: JSON.stringify(body) });
      }
      showToast("Gespeichert ✓");
      setAdminMaModal(null);
      loadAdminMitarbeiter();
    } catch (e) {
      showToast("Fehler: " + e.message, "error");
    }
    setAdminMaSaving(false);
  };

  const handleAdminKonfigSave = async () => {
    if (!adminKonfigMaId) return;
    setAdminKonfigSaving(true);
    try {
      await api(`/admin/mitarbeiter/${adminKonfigMaId}/config`, { method: "PUT", body: JSON.stringify(adminKonfig) });
      showToast("Konfiguration gespeichert ✓");
    } catch (e) {
      showToast("Fehler: " + e.message, "error");
    }
    setAdminKonfigSaving(false);
  };

  const adminAddProjekt = () => {
    if (!adminNewProjekt.label) return;
    const entry = { ...adminNewProjekt };
    if (adminEditProjektIdx !== null) {
      setAdminKonfig((k) => ({ ...k, projekte: k.projekte.map((p, i) => i === adminEditProjektIdx ? entry : p) }));
    } else {
      setAdminKonfig((k) => ({ ...k, projekte: [...k.projekte, entry] }));
    }
    setAdminEditProjektIdx(null);
    setAdminNewProjekt({ label: "", kuerzel: "", sort_order: 0 });
    setAdminShowNewProjekt(false);
  };
  const adminEditProjekt = (i) => {
    const p = adminKonfig.projekte[i];
    setAdminNewProjekt({
      label: p.label || "",
      kuerzel: p.kuerzel || "",
      sort_order: p.sort_order ?? 0,
    });
    setAdminEditProjektIdx(i);
    setAdminShowNewProjekt(true);
  };

  const adminRemoveProjekt = (i) => {
    setAdminKonfig(k => ({ ...k, projekte: k.projekte.filter((_, idx) => idx !== i) }));
    if (adminEditProjektIdx === i) {
      setAdminEditProjektIdx(null);
      setAdminShowNewProjekt(false);
    }
  };

  const adminAddFeld = () => {
    if (!adminNewFeld.feld_name) return;
    // optionen ist im Form als kommagetrennter String. Backend erwartet JSON-Array
    // als String. Normalisieren beim Speichern.
    const optionenList = (adminNewFeld.optionen || "")
      .split(",").map((s) => s.trim()).filter(Boolean);
    const optionenStr = optionenList.length ? JSON.stringify(optionenList) : null;
    const entry = { ...adminNewFeld, optionen: optionenStr };
    if (adminEditFeldIdx !== null) {
      setAdminKonfig((k) => ({ ...k, felder: k.felder.map((f, idx) => idx === adminEditFeldIdx ? entry : f) }));
    } else {
      setAdminKonfig((k) => ({ ...k, felder: [...k.felder, entry] }));
    }
    setAdminEditFeldIdx(null);
    setAdminNewFeld({ feld_name: "", feld_label: "", feld_typ: "text", optionen: "", sichtbar: true, pflichtfeld: false });
    setAdminShowNewFeld(false);
    return;
  };
  const adminEditFeld = (i) => {
    const f = adminKonfig.felder[i];
    let optionenStr = "";
    if (f.optionen) {
      try {
        const arr = typeof f.optionen === "string" ? JSON.parse(f.optionen) : f.optionen;
        if (Array.isArray(arr)) optionenStr = arr.join(", ");
      } catch { optionenStr = String(f.optionen || ""); }
    }
    setAdminNewFeld({
      feld_name: f.feld_name || "",
      feld_label: f.feld_label || "",
      feld_typ: f.feld_typ || "text",
      optionen: optionenStr,
      sichtbar: f.sichtbar !== false,
      pflichtfeld: !!f.pflichtfeld,
    });
    setAdminEditFeldIdx(i);
    setAdminShowNewFeld(true);
  };
  const adminRemoveFeld = (i) => {
    setAdminKonfig(k => ({ ...k, felder: k.felder.filter((_, idx) => idx !== i) }));
    if (adminEditFeldIdx === i) {
      setAdminEditFeldIdx(null);
      setAdminShowNewFeld(false);
    }
  };

  const adminAddBereich = () => {
    if (!adminNewBereich.bereich_label) return;
    const entry = { ...adminNewBereich };
    if (adminEditBereichIdx !== null) {
      setAdminKonfig((k) => ({ ...k, bereiche: k.bereiche.map((b, i) => i === adminEditBereichIdx ? entry : b) }));
    } else {
      setAdminKonfig((k) => ({ ...k, bereiche: [...k.bereiche, entry] }));
    }
    setAdminEditBereichIdx(null);
    setAdminNewBereich({ bereich_label: "", sichtbar: true });
    setAdminShowNewBereich(false);
  };
  const adminEditBereich = (i) => {
    const b = adminKonfig.bereiche[i];
    setAdminNewBereich({ bereich_label: b.bereich_label || "", sichtbar: b.sichtbar !== false });
    setAdminEditBereichIdx(i);
    setAdminShowNewBereich(true);
  };

  const adminRemoveBereich = (i) => {
    setAdminKonfig(k => ({ ...k, bereiche: k.bereiche.filter((_, idx) => idx !== i) }));
  };

  const statForYear = (arr, y, key) => (arr.find((r) => r.jahr === y)?.[key] ?? 0);

  // ── Berechnungen Zeiterfassung ──
  const currentPauseMin = pauseActive
    ? pauseMin + Math.floor((Date.now() - (pauseStartRef.current || Date.now())) / 60000)
    : pauseMin;

  const arbStunden = calcH(zeitVon, zeitBis, currentPauseMin);

  // Live-Stechuhr: wenn Start gesetzt, aber noch kein Schluss → live laufende Zeit
  const liveArbStunden = (() => {
    if (!zeitVon || zeitBis) return null; // nicht aktiv oder schon beendet
    const vonMin = timeStrToMin(zeitVon);
    if (vonMin === null) return null;
    const nowMin = uhr.getHours() * 60 + uhr.getMinutes() + uhr.getSeconds() / 60;
    return Math.max(0, (nowMin - vonMin - currentPauseMin) / 60);
  })();

  // Datum-Label für den gewählten Tag
  const selectedDate = new Date(selectedDatum + "T12:00:00");
  const isToday = selectedDatum === new Date().toISOString().slice(0, 10);
  const tagName = DAYS_DE[selectedDate.getDay()];
  const datumLabel = `${tagName}, ${selectedDate.getDate()}. ${MONTHS_DE[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;
  const uhrzeitStr = `${String(uhr.getHours()).padStart(2,"0")}:${String(uhr.getMinutes()).padStart(2,"0")}:${String(uhr.getSeconds()).padStart(2,"0")}`;

  return (
    <div className="anz-page">
      {/* ── Header ── */}
      <div className="anz-header">
        <div className="anz-header-left">
          <h1 className="anz-title">Arbeitsnachweis</h1>
          <p className="anz-subtitle">
            Zeiterfassung · Releases · Statistik
            {maInfo && <span style={{marginLeft:"8px",fontWeight:600,color:"var(--color-primary)"}}>{maInfo.kuerzel}</span>}
          </p>
        </div>
        <div className="anz-header-right">
          {maInfo?.zugang_typ !== "nur_zeiterfassung" && (
            <select className="anz-select" value={jahr} onChange={(e) => setJahr(+e.target.value)}>
              {[2024, 2025, 2026].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          <button className="anz-btn anz-btn-export" onClick={openSelfExport}>
            ⬇ Export
          </button>
          <button className="anz-logout-btn" onClick={handleLogout} title="Ausloggen">
            Logout
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      {!adminOnly && (
        <div className="anz-tabs">
          {[
            ...(!istNurAdmin ? [
              { id: "zeiterfassung", label: "Zeiterfassung" },
              { id: "tage",          label: "Tage" },
              { id: "antraege",      label: "📨 Anträge" + (antraegePendingCount > 0 ? ` (${antraegePendingCount})` : "") },
              { id: "statistik",     label: "Statistik" },
              ...(maInfo?.zugang_typ !== "nur_zeiterfassung" ? [{ id: "releases", label: "Releases" }] : []),
            ] : []),
            ...(maInfo?.rolle === "admin" ? [{ id: "admin", label: "⚙ Admin" }] : []),
          ].map((t) => (
            <button
              key={t.id}
              className={`anz-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {error && <div className="anz-error">⚠ {error}</div>}

      {/* ── TAB: ZEITERFASSUNG ── */}
      {tab === "zeiterfassung" && (
        <div className="anz-tab-content anz-tab-content-center">
          <div className="zeit-card">
            {/* Datum + Uhr */}
            <div className="zeit-header">
              <div className="zeit-datum zeit-datum-klickbar" title="Datum wählen">
                <span>{datumLabel}</span>
                {!isToday && <span className="zeit-datum-badge">anderer Tag</span>}
                <input
                  ref={datepickerRef}
                  type="date"
                  className="zeit-datepicker-overlay"
                  value={selectedDatum}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => e.target.value && handleDatumChange(e.target.value)}
                />
              </div>
              <div className="zeit-uhr">
                {/* Stundensaldo + Resturlaub leben jetzt im Statistik-Tab.
                    Header bleibt schlank — nur Auto-Save-Status + Uhrzeit. */}
                {saveState !== "idle" && (
                  <span className={`zeit-save-badge zeit-save-${saveState}`} title={saveError}>
                    {saveState === "saving" ? "speichert…"
                      : saveState === "saved" ? "✓ gespeichert"
                      : saveState === "error" ? "⚠ Fehler"
                      : "Änderung…"}
                  </span>
                )}
                {uhrzeitStr}
              </div>
            </div>

            {/* ── Vorlagen-Banner (Welle 7.4): nur wenn Tag leer + Vorlage existiert ── */}
            {heuteDefault && (
              <div className="zeit-vorlage-banner">
                <div className="zeit-vorlage-text">
                  <strong>📋 Vorlage für {heuteDefault.wotag_label || "diesen Tag"}:</strong>{" "}
                  {[
                    heuteDefault.art,
                    heuteDefault.von && heuteDefault.bis ? `${heuteDefault.von}–${heuteDefault.bis}` : null,
                    heuteDefault.pause_min ? `Pause ${heuteDefault.pause_min} Min` : null,
                    heuteDefault.arbeitsort,
                    heuteDefault.projekt_kuerzel,
                    heuteDefault.bereiche?.length ? heuteDefault.bereiche.join(", ") : null,
                  ].filter(Boolean).join(" · ") || "leer"}
                </div>
                <div className="zeit-vorlage-actions">
                  <button className="anz-btn anz-btn-sm anz-btn-save"
                    onClick={() => {
                      const d = heuteDefault;
                      if (d.art)                          setArtHeute(d.art);
                      if (d.von)                          setZeitVon(d.von);
                      if (d.bis)                          setZeitBis(d.bis);
                      if (d.pause_min != null)            { setPauseMin(d.pause_min); pauseAccRef.current = d.pause_min; }
                      if (d.arbeitsort)                   setOrtHeute(d.arbeitsort);
                      if (d.projekt_kuerzel)              setProjektHeute(d.projekt_kuerzel);
                      if (d.bereiche?.length)             setBereicheHeute(d.bereiche);
                      setHeuteDefault(null);  // Banner schließen → Auto-Save triggert über State-Änderung
                    }}>✓ Übernehmen</button>
                  <button className="anz-btn anz-btn-sm secondary" onClick={() => setHeuteDefault(null)}>
                    Verwerfen
                  </button>
                </div>
              </div>
            )}

            {/* Schnellübernahme — dieselbe Funktion wie im Bearbeiten-Fenster
                der Tagesliste, damit man nicht erst dorthin wechseln muss. */}
            <div className="anz-uebernehmen">
              <span className="anz-uebernehmen-label">Übernehmen von:</span>
              <button type="button" className="anz-btn-klein"
                      onClick={() => zeitUebernehmenVon(1)}
                      title="Art, Ort, Zeiten, Pause, Tätigkeit und Text vom letzten Arbeitstag übernehmen">
                ← gestern
              </button>
              <button type="button" className="anz-btn-klein"
                      onClick={() => zeitUebernehmenVon(7)}
                      title="Vom gleichen Wochentag der Vorwoche übernehmen">
                ← letzte Woche
              </button>
            </div>

            {/* ── Sektion: Basisdaten ── */}
            <div className="zeit-section-label">Basisdaten</div>
            {/* Art + Ort + Projekt */}
            <div className="zeit-row">
              <div className="zeit-field">
                <label className="zeit-label">Art</label>
                <select className="anz-select zeit-select-art"
                  value={artHeute} onChange={(e) => setArtHeute(e.target.value)}>
                  <option value="Arbeit">Arbeit</option>
                  <option value="Krank">Krank</option>
                  <option value="Urlaub">Urlaub</option>
                  <option value="Feiertag">Feiertag</option>
                </select>
              </div>
              {artHeute === "Arbeit" && (
                <div className="zeit-field">
                  <label className="zeit-label">Ort</label>
                  <select className="anz-select zeit-select-art"
                    value={ortHeute} onChange={(e) => setOrtHeute(e.target.value)}>
                    {(maInfo?.orte?.length > 0 ? maInfo.orte : ["Homeoffice", "Bremen"]).map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              )}
              {artHeute === "Arbeit" && maInfo?.projekte?.length > 0 && (
                <div className="zeit-field">
                  <label className="zeit-label">Projekt</label>
                  <select className="anz-select zeit-select-art"
                    value={projektHeute} onChange={(e) => setProjektHeute(e.target.value)}>
                    <option value="">– kein –</option>
                    {maInfo.projekte.map(p => (
                      <option key={p.p_kuerzel || p.label} value={p.p_kuerzel || p.label}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* ── Sektion: Benutzerdefinierte Felder ── */}
            {artHeute === "Arbeit" && (maInfo?.felder?.length > 0 || maInfo?.bereiche?.length > 0) && (
              <>
                <div className="zeit-section-label">Zusätzliche Felder</div>

                {/* Bereiche als Checkboxen */}
                {maInfo?.bereiche?.length > 0 && (
                  <div className="zeit-field zeit-field-full">
                    <label className="zeit-label">Bereiche</label>
                    <div className="zeit-bereiche-row">
                      {maInfo.bereiche.map(b => (
                        <label key={b.id} className="zeit-bereich-check">
                          <input type="checkbox"
                            checked={bereicheHeute.includes(b.bereich_label)}
                            onChange={e => {
                              if (e.target.checked) setBereicheHeute(prev => [...prev, b.bereich_label]);
                              else setBereicheHeute(prev => prev.filter(x => x !== b.bereich_label));
                            }}
                          />
                          {b.bereich_label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extra-Felder: Text, Select, Multiselect, Number, Decimal, Checkbox */}
                {maInfo?.felder?.length > 0 && (
                  <div className="zeit-dynamic-felder">
                    {maInfo.felder.map(f => {
                      const optionen = (() => { try { return f.optionen ? JSON.parse(f.optionen) : []; } catch { return []; } })();
                      const currentArr = (() => {
                        const v = extraFelder[f.feld_name];
                        if (Array.isArray(v)) return v;
                        if (typeof v === "string" && v.startsWith("[")) {
                          try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
                        }
                        return [];
                      })();
                      const toggleMs = (opt) => {
                        const next = currentArr.includes(opt) ? currentArr.filter(x => x !== opt) : [...currentArr, opt];
                        setExtraFelder(prev => ({ ...prev, [f.feld_name]: JSON.stringify(next) }));
                      };
                      const triCycle = () => {
                        const cur = String(extraFelder[f.feld_name] || "");
                        const next = cur === "" ? "0.5" : cur === "0.5" ? "1" : "";
                        setExtraFelder(prev => ({ ...prev, [f.feld_name]: next }));
                      };
                      const triState = String(extraFelder[f.feld_name] || "");
                      return (
                        <div key={f.feld_name} className={`zeit-field ${(f.feld_typ === "checkbox" || f.feld_typ === "tristate") ? "zeit-field-checkbox" : ""}`}>
                          {f.feld_typ === "checkbox" ? (
                            <label className="zeit-checkbox-label">
                              <input type="checkbox"
                                checked={!!(extraFelder[f.feld_name])}
                                onChange={e => setExtraFelder(prev => ({ ...prev, [f.feld_name]: e.target.checked ? "1" : "" }))} />
                              <span>{f.feld_label || f.feld_name}</span>
                            </label>
                          ) : f.feld_typ === "tristate" ? (
                            <button type="button" className={`zeit-tristate zeit-tristate-${triState === "1" ? "voll" : triState === "0.5" ? "halb" : "leer"}`}
                              onClick={triCycle}
                              title="Klick: aus → ½ → ✓ → aus">
                              <span className="zeit-tristate-mark">
                                {triState === "1" ? "✓" : triState === "0.5" ? "½" : "·"}
                              </span>
                              <span>{f.feld_label || f.feld_name}</span>
                            </button>
                          ) : (
                            <>
                              <label className="zeit-label">{f.feld_label || f.feld_name}</label>
                              {f.feld_typ === "select" ? (
                                <select className="anz-select"
                                  value={extraFelder[f.feld_name] || ""}
                                  onChange={e => setExtraFelder(prev => ({ ...prev, [f.feld_name]: e.target.value }))}>
                                  <option value="">– wählen –</option>
                                  {optionen.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : f.feld_typ === "multiselect" ? (
                                <div className="zeit-multiselect">
                                  {optionen.length === 0 && <span className="re-muted">(Admin: bitte Optionen pflegen)</span>}
                                  {optionen.map(o => (
                                    <button type="button" key={o}
                                      className={`zeit-multi-btn ${currentArr.includes(o) ? "active" : ""}`}
                                      onClick={() => toggleMs(o)}>{o}</button>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  type={(f.feld_typ === "number" || f.feld_typ === "decimal") ? "number" : "text"}
                                  step={f.feld_typ === "decimal" ? "0.5" : undefined}
                                  className="zeit-time-input zeit-text-input"
                                  value={extraFelder[f.feld_name] || ""}
                                  onChange={e => setExtraFelder(prev => ({ ...prev, [f.feld_name]: e.target.value }))} />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── Sektion: Zeit ── */}
            <div className="zeit-section-label">Zeit</div>
            {/* Timer-Buttons */}
            <div className="zeit-timer-row">
              {/* Start */}
              <div className="zeit-timer-block">
                <div className="zeit-timer-label">Start</div>
                <div className="zeit-timer-input-row">
                  <input type="time" step="900" className="zeit-time-input" value={zeitVon}
                    onChange={(e) => setZeitVon(e.target.value)} />
                  {isToday && (
                    <button
                      className={`zeit-btn-now ${zeitVon && !zeitBis ? "zeit-btn-now-active" : ""}`}
                      onClick={handleStart}
                      disabled={!!zeitVon}
                      title={zeitVon ? "Start bereits gesetzt" : "Aktuelle Zeit eintragen + sofort speichern"}
                    >
                      Jetzt
                    </button>
                  )}
                </div>
              </div>

              {/* Pause */}
              <div className="zeit-timer-block">
                <div className="zeit-timer-label">Pause</div>
                {isToday ? (
                  <>
                    <button
                      className={`zeit-btn ${pauseActive ? "zeit-btn-pause-stop" : "zeit-btn-pause"}`}
                      onClick={handlePauseToggle}
                      disabled={!zeitVon && !pauseActive}
                    >
                      {pauseActive ? "⏸ Ende" : "⏸ Pause"}
                    </button>
                    {(currentPauseMin > 0 || pauseActive) && (
                      <div className="zeit-timer-sub">{minToHHMM(currentPauseMin)} h</div>
                    )}
                  </>
                ) : (
                  <input
                    type="time"
                    step="900"
                    className="zeit-time-input"
                    value={pauseMin > 0 ? `${String(Math.floor(pauseMin/60)).padStart(2,"0")}:${String(pauseMin%60).padStart(2,"0")}` : ""}
                    onChange={(e) => {
                      if (!e.target.value) { setPauseMin(0); pauseAccRef.current = 0; return; }
                      const [h, m] = e.target.value.split(":").map(Number);
                      const min = (h||0)*60 + (m||0);
                      setPauseMin(min);
                      pauseAccRef.current = min;
                    }}
                    title="Pause-Dauer eingeben (z.B. 00:30 für 30 Min)"
                  />
                )}
              </div>

              {/* Schluss */}
              <div className="zeit-timer-block">
                <div className="zeit-timer-label">Schluss</div>
                <div className="zeit-timer-input-row">
                  <input type="time" step="900" className="zeit-time-input" value={zeitBis}
                    onChange={(e) => setZeitBis(e.target.value)} />
                  {isToday && (
                    <button className="zeit-btn-now" onClick={handleSchluss} disabled={!zeitVon} title="Aktuelle Zeit eintragen">
                      Jetzt
                    </button>
                  )}
                </div>
              </div>

              {/* Ergebnis / Stechuhr */}
              <div className="zeit-timer-block zeit-ergebnis-block">
                <div className="zeit-timer-label">Arbeitszeit</div>
                {liveArbStunden !== null ? (
                  // Live laufende Stechuhr
                  <div className="zeit-stunden zeit-stunden-live">
                    {Math.floor(liveArbStunden)}:{String(Math.floor((liveArbStunden % 1) * 60)).padStart(2,"0")} h
                    <span className="zeit-stunden-dot">●</span>
                  </div>
                ) : arbStunden !== null ? (
                  <div className="zeit-stunden">{arbStunden.toFixed(2)} h</div>
                ) : (
                  <div className="zeit-stunden-leer">–</div>
                )}
              </div>
            </div>

            {/* Heutige Releases */}
            {heuteReleases.length > 0 && (
              <div className="zeit-releases">
                <div className="zeit-releases-label">Releases heute</div>
                <div className="zeit-releases-list">
                  {heuteReleases.map((r) => (
                    <span key={r.id} className="zeit-release-chip">
                      v{r.release_version}{r.was ? ` – ${r.was}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Was habe ich gemacht */}
            <div className="zeit-field zeit-field-full">
              <label className="zeit-label">Was habe ich gemacht</label>
              <textarea
                className="zeit-textarea"
                rows={4}
                placeholder="Tätigkeiten, Aufgaben, Projekte …"
                value={wasHeute}
                onChange={(e) => setWasHeute(e.target.value)}
              />
            </div>

            {/* Auto-Save Footer */}
            <div className="zeit-footer">
              <span className="zeit-autosave-hint">
                {zeitSaving ? "⟳ Speichert …" : "✓ Automatisch gespeichert"}
              </span>
              <button className="anz-btn anz-btn-save anz-btn-save-manual" onClick={handleSaveHeute} disabled={zeitSaving}>
                Jetzt speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: TAGE ── */}
      {tab === "tage" && (
        <div className="anz-tab-content">
          {/* Filter */}
          <div className="anz-tage-filter">
            <div className="anz-filter-group">
              {[["monat","Monat"],["jahr","Jahr"],["alles","Alles"]].map(([v,l]) => (
                <button key={v}
                  className={`anz-filter-btn ${tageFilter === v ? "active" : ""}`}
                  onClick={() => setTageFilter(v)}>
                  {l}
                </button>
              ))}
            </div>
            {tageFilter !== "alles" && (
              <select className="anz-select" value={jahr} onChange={(e) => setJahr(+e.target.value)}>
                {[2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {tageFilter === "monat" && (
              <select className="anz-select" value={monat} onChange={(e) => setMonat(+e.target.value)}>
                {MONTHS_DE.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            )}
            {tageFilter === "monat" && (
              <button
                className="anz-btn anz-btn-init-monat"
                onClick={handleInitMonat}
                disabled={initMonatLoading}
                title={`Alle fehlenden Tage für ${MONTHS_DE[monat-1]} ${jahr} anlegen (Wochenenden + Feiertage)`}
              >
                {initMonatLoading ? "…" : "📅 Monat befüllen"}
              </button>
            )}
            <span className="anz-tage-count">{eintraege.length} Einträge</span>
            {/* Ansicht-Toggle */}
            <div className="admin-konfig-tabs" style={{margin:0, padding:2, marginLeft:"auto"}}>
              {[["liste","📋 Liste"], ["woche","🗓 Woche"]].map(([id, label]) => (
                <button key={id}
                  className={`admin-konfig-tab ${tageView === id ? "active" : ""}`}
                  onClick={() => setTageView(id)}>{label}</button>
              ))}
            </div>
          </div>

          {/* Einzelnes Datum anlegen */}
          <div className="anz-datum-anlegen">
            <span className="anz-datum-anlegen-label">Datum anlegen:</span>
            <input
              type="date"
              className="anz-modal-input"
              id="datum-anlegen-input"
              defaultValue={new Date().toISOString().slice(0,10)}
            />
            <button
              className="anz-btn anz-btn-sm"
              onClick={() => {
                const val = document.getElementById("datum-anlegen-input").value;
                if (val) handleEintragAnlegen(val);
              }}
            >
              Anlegen
            </button>
          </div>

          {copyMode && (
            <div className="anz-copy-bar">
              <span className="anz-copy-bar-info">
                📋 Kopieren: <strong>{copyMode.datum}</strong> ({copyModus === "zusatz" ? "als weitere Tätigkeit" : "Tag füllen"})
                {" · "}Zieltage anhaken — <strong>{copyApplied.size}</strong> gewählt
              </span>
              <div className="anz-copy-bar-actions">
                <button className="anz-btn anz-btn-sm" disabled={copySaving || copyApplied.size === 0}
                        onClick={confirmCopyMode}>
                  {copySaving ? "Speichere…" : `✓ Übernehmen (${copyApplied.size})`}
                </button>
                <button className="anz-btn anz-btn-sm secondary" disabled={copySaving}
                        onClick={exitCopyMode}>Abbrechen</button>
              </div>
            </div>
          )}
          {loading && <TageSkeleton />}
          {!loading && eintraege?.length > 0 && (
            <TagesSumme eintraege={eintraege} regelarbeitszeit_h={stundensaldo?.regelarbeitszeit_h} />
          )}
          {tageView === "woche" && !loading && (
            <WeekGrid eintraege={eintraege} onEdit={openEdit} />
          )}
          {tageView === "liste" && (
            <TagesTable
              eintraege={eintraege}
              loading={loading}
              expandedDays={expandedDays}
              toggleDayExpand={toggleDayExpand}
              onEdit={openEdit}
              onAddTaetigkeit={handleAddTaetigkeit}
              onKopieren={startCopyMode}
              onDelete={handleDeleteEintrag}
              onReleaseClick={setReleaseDetail}
              regelarbeitszeit_h={stundensaldo?.regelarbeitszeit_h}
              copyMode={copyMode}
              copyModus={copyModus}
              copyApplied={copyApplied}
              onCopyTarget={handleCopyTarget}
              onExitCopy={exitCopyMode}
              onConfirmCopy={confirmCopyMode}
              copySaving={copySaving}
            />
          )}
        </div>
      )}

      {/* ── Edit-Modal ── */}
      {editModal && (
        // Bewusst KEIN onClick auf dem Overlay: Schließen nur über
        // Abbrechen/Speichern — sonst geht die Eingabe beim Text-Markieren verloren.
        <div className="anz-modal-overlay">
          <div className="anz-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="anz-modal-header">
              <h3 className="anz-modal-title">Eintrag bearbeiten</h3>
              <div className="anz-modal-datum">{editModal.datum}</div>
            </div>

            {/* Schnellübernahme: füllt das Formular aus einem früheren Tag.
                Gespeichert wird erst mit „Speichern" — so lässt sich vorher
                noch anpassen, statt den Tag blind zu überschreiben. */}
            <div className="anz-uebernehmen">
              <span className="anz-uebernehmen-label">Übernehmen von:</span>
              <button type="button" className="anz-btn-klein"
                      onClick={() => uebernehmenVon(1)}
                      title="Art, Ort, Zeiten, Pause, Tätigkeit und Text vom letzten Arbeitstag übernehmen">
                ← gestern
              </button>
              <button type="button" className="anz-btn-klein"
                      onClick={() => uebernehmenVon(7)}
                      title="Vom gleichen Wochentag der Vorwoche übernehmen">
                ← letzte Woche
              </button>
            </div>

            {/* Aufbau wie das Zeiterfassungs-Formular:
                Basisdaten → Zusätzliche Felder → Zeit → Was */}
            <div className="anz-modal-section-label">Basisdaten</div>
            <div className="anz-modal-grid">
              <div className="anz-modal-field">
                <label>Art</label>
                <select className="anz-select" value={editForm.art}
                  onChange={(e) => {
                    const newArt = e.target.value;
                    const clearFields = ["Krank","Urlaub","Feiertag","Wochenende"];
                    if (clearFields.includes(newArt)) {
                      setEditForm(f => ({...f, art: newArt, arbeitsort: "", von: "", bis: "", pause_min: 0, arbeitszeit_h: "", was: "", taetigkeit: "", epic: ""}));
                    } else {
                      setEditForm(f => ({...f, art: newArt}));
                    }
                  }}>
                  {["Arbeit","Krank","Urlaub","Feiertag","Wochenende"].map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
              <div className="anz-modal-field">
                <label>Ort</label>
                <select className="anz-select" value={editForm.arbeitsort}
                  onChange={(e) => setEditForm(f => ({...f, arbeitsort: e.target.value}))}>
                  <option value="">–</option>
                  {(() => {
                    // Gleiche Liste wie auf der Zeiterfassung-Seite (ma_orte);
                    // ein abweichender Bestandswert bleibt als Option erhalten
                    const orte = maInfo?.orte?.length > 0 ? [...maInfo.orte] : ["Homeoffice", "Bremen"];
                    if (editForm.arbeitsort && !orte.includes(editForm.arbeitsort)) {
                      orte.push(editForm.arbeitsort);
                    }
                    return orte.map(o => <option key={o} value={o}>{o}</option>);
                  })()}
                </select>
              </div>
            </div>

            <div className="anz-modal-section-label">Zusätzliche Felder</div>
            <div className="anz-modal-grid">
              <div className="anz-modal-field">
                <label>Tätigkeit</label>
                <input type="text" className="anz-modal-input" value={editForm.taetigkeit}
                  onChange={(e) => setEditForm(f => ({...f, taetigkeit: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Epic/Thema</label>
                <input type="text" className="anz-modal-input" value={editForm.epic}
                  onChange={(e) => setEditForm(f => ({...f, epic: e.target.value}))} />
              </div>
            </div>

            <div className="anz-modal-section-label">Zeit</div>
            <div className="anz-modal-grid">
              <div className="anz-modal-field">
                <label>Von</label>
                <input type="time" step="900" className="anz-modal-input" value={editForm.von}
                  onChange={(e) => setEditForm(f => ({...f, von: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Bis</label>
                <input type="time" step="900" className="anz-modal-input" value={editForm.bis}
                  onChange={(e) => setEditForm(f => ({...f, bis: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Pause (Min)</label>
                <input type="number" className="anz-modal-input" min="0" max="240"
                  value={editForm.pause_min}
                  onChange={(e) => setEditForm(f => ({...f, pause_min: +e.target.value}))} />
              </div>
              {(!editForm.von || !editForm.bis) && (
                <div className="anz-modal-field">
                  <label>Stunden</label>
                  <input type="number" step="0.25" className="anz-modal-input"
                    value={editForm.arbeitszeit_h}
                    onChange={(e) => setEditForm(f => ({...f, arbeitszeit_h: e.target.value}))} />
                </div>
              )}
            </div>

            <div className="anz-modal-field-full">
              <label>Was habe ich gemacht</label>
              <textarea className="zeit-textarea" rows={3} value={editForm.was}
                onChange={(e) => setEditForm(f => ({...f, was: e.target.value}))} />
            </div>

            <div className="anz-modal-footer">
              <button className="anz-btn anz-btn-cancel" onClick={() => setEditModal(null)}>Abbrechen</button>
              <button className="anz-btn anz-btn-save" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? "Speichert …" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Release-Detail-Modal ── */}
      {releaseDetail && (
        <div className="anz-modal-overlay" onClick={() => setReleaseDetail(null)}>
          <div className="anz-modal" style={{maxWidth: 620}} onClick={(ev) => ev.stopPropagation()}>
            <div className="anz-modal-header">
              <h3 className="anz-modal-title">🚀 Releases am {releaseDetail.datum}</h3>
              <button className="anz-edit-btn" onClick={() => setReleaseDetail(null)}>✕</button>
            </div>
            {releaseDetail.releases.map((r, i) => (
              <div key={i} className="anz-release-detail-item">
                <div className="anz-release-detail-version">v{r.version}</div>
                {r.titel && <div className="anz-release-detail-title">{r.titel}</div>}
                {r.details && (
                  <div className="anz-release-detail-desc">{r.details}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: STATISTIK ── */}
      {tab === "antraege" && (
        <div className="anz-tab-content">
          <div className="admin-section-header" style={{flexWrap:"wrap",gap:8}}>
            <h2 className="anz-section-title" style={{margin:0}}>
              Meine Anträge <span className="admin-count-badge">{antraege.length}</span>
            </h2>
            <button className="anz-btn admin-btn-new"
              onClick={() => { setAntrForm({ art: "Urlaub", von: "", bis: "", anmerkung: "" }); setAntrModal({}); }}>
              + Neuer Antrag
            </button>
          </div>
          <div className="tbl-wrap" style={{marginTop:12}}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Art</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th className="r">Werktage</th>
                  <th>Anmerkung</th>
                  <th>Status</th>
                  <th>Entscheidung</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {antraege.length === 0 && (
                  <tr><td colSpan={8} className="tbl-empty">Keine Anträge</td></tr>
                )}
                {antraege.map((a) => (
                  <tr key={a.id}>
                    <td><span className={`badge badge-antrag-${a.art.toLowerCase()}`}>{a.art}</span></td>
                    <td>{a.von}</td>
                    <td>{a.bis}</td>
                    <td className="r">{a.anzahl_tage}</td>
                    <td>{a.anmerkung || "–"}</td>
                    <td><span className={`badge badge-antrag-status-${a.status}`}>{a.status}</span></td>
                    <td>
                      {a.entscheidung_am ? (
                        <>
                          <div style={{fontSize:12}}>{a.entscheidung_am.slice(0,16)}</div>
                          {a.entscheidung_kommentar && (
                            <div style={{fontSize:12, color:"var(--color-gray-500)"}}>{a.entscheidung_kommentar}</div>
                          )}
                        </>
                      ) : "–"}
                    </td>
                    <td className="td-edit">
                      {a.status === "offen" && (
                        <button className="admin-del-btn" title="Zurückziehen"
                          onClick={() => zurueckziehen(a.id)}>✕</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Antrags-Modal */}
      {antrModal && (
        <div className="anz-modal-overlay" onClick={() => setAntrModal(null)}>
          <div className="anz-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="anz-modal-header">
              <h3 className="anz-modal-title">Neuer Antrag</h3>
              <button className="anz-modal-close" onClick={() => setAntrModal(null)}>✕</button>
            </div>
            <div className="anz-modal-body">
              <div className="anz-modal-field">
                <label>Art</label>
                <select className="anz-select" value={antrForm.art}
                  onChange={(e) => setAntrForm(f => ({...f, art: e.target.value}))}>
                  <option value="Urlaub">Urlaub</option>
                  <option value="Krank">Krank</option>
                  <option value="Sonderurlaub">Sonderurlaub</option>
                </select>
              </div>
              <div className="anz-modal-field">
                <label>Von</label>
                <input type="date" className="anz-modal-input" value={antrForm.von}
                  onChange={(e) => setAntrForm(f => ({...f, von: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Bis</label>
                <input type="date" className="anz-modal-input" value={antrForm.bis}
                  onChange={(e) => setAntrForm(f => ({...f, bis: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Anmerkung (optional)</label>
                <textarea className="anz-modal-input" rows={3} value={antrForm.anmerkung}
                  onChange={(e) => setAntrForm(f => ({...f, anmerkung: e.target.value}))} />
              </div>
            </div>
            <div className="anz-modal-footer">
              <button className="anz-btn" onClick={() => setAntrModal(null)}>Abbrechen</button>
              <button className="anz-btn anz-btn-save" onClick={submitAntrag} disabled={antrSaving}>
                {antrSaving ? "Sende…" : "Antrag stellen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "statistik" && (
        <div className="anz-tab-content">
          {/* Stunden-Konto: Soll/Ist + Resturlaub + Erklärung (Welle 8). */}
          {stundensaldo && (
            <div className="stat-saldo-card">
              <div className="stat-saldo-title">Stundenkonto {stundensaldo.jahr}</div>
              <div className="stat-saldo-row">
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Saldo gesamt</span>
                  <span className={`stat-saldo-value ${(stundensaldo.saldo_gesamt ?? stundensaldo.saldo_h) >= 0 ? "pos" : "neg"}`}>
                    {(stundensaldo.saldo_gesamt ?? stundensaldo.saldo_h) >= 0 ? "+" : ""}{(stundensaldo.saldo_gesamt ?? stundensaldo.saldo_h).toFixed(1)} h
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Jahr {stundensaldo.jahr}</span>
                  <span className={`stat-saldo-value ${stundensaldo.saldo_h >= 0 ? "pos" : "neg"}`}>
                    {stundensaldo.saldo_h >= 0 ? "+" : ""}{stundensaldo.saldo_h.toFixed(1)} h
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Übertrag/Vorjahr</span>
                  <span className="stat-saldo-value">{(stundensaldo.vorjahres_saldo ?? 0).toFixed(1)} h</span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Anrechenbar</span>
                  <span className="stat-saldo-value">
                    {(stundensaldo.anrechenbar_h ?? stundensaldo.ist_h).toFixed(1)} h
                    <span className="stat-saldo-sub"> ({stundensaldo.ist_h.toFixed(1)} + {(stundensaldo.gutschrift_h ?? 0).toFixed(1)} Gutschrift)</span>
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Soll</span>
                  <span className="stat-saldo-value">
                    {stundensaldo.soll_h.toFixed(1)} h
                    <span className="stat-saldo-sub"> ({stundensaldo.soll_arbeitstage ?? "–"} Tage)</span>
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Urlaubs-Saldo</span>
                  <span className="stat-saldo-value">
                    {stundensaldo.saldo_urlaub ?? stundensaldo.urlaub_rest} <span className="stat-saldo-sub">Tage</span>
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Tagessoll (Mo–Fr)</span>
                  <span className="stat-saldo-value">
                    {stundensaldo.tagessoll_h ?? stundensaldo.regelarbeitszeit_h} h <span className="stat-saldo-sub">/ Tag</span>
                  </span>
                </div>
                <div className="stat-saldo-pill">
                  <span className="stat-saldo-label">Bundesland</span>
                  <span className="stat-saldo-value">{stundensaldo.bundesland}</span>
                </div>
              </div>
              <div className="stat-saldo-erklaerung">
                {stundensaldo.erklaerung}
              </div>
            </div>
          )}
          {/* Kalender-Abo: URL mit Token, kopierbar */}
          <CalendarSubscribeBox token={maToken} />
          <h2 className="anz-section-title">Monatliche Übersicht {jahr}</h2>
          <div className="anz-table-wrap strg-table-scroll" style={{marginBottom: 28}}>
            <table className="anz-table strg-table">
              <thead>
                <tr>
                  <th>Monat</th>
                  <th className="num" title="Erbrachte Stunden inkl. Sa/So">Erb. Std</th>
                  <th className="num" title="Feiertage Mo–Fr (Kalender)">FT</th>
                  <th className="num" title="Krank-Tage Mo–Fr">Krank</th>
                  <th className="num" title="Urlaub genommen Mo–Fr">Urlaub</th>
                  <th className="num" title="(FT + Krank + Urlaub) × Tagessoll">U/F/K Std</th>
                  <th className="num" title="Erbracht + Gutschrift">Σ Std</th>
                  <th className="num" title="Werktage − Feiertage">Soll-AT</th>
                  <th className="num">Soll Std</th>
                  <th className="num" title="Summe − Soll">Diff.</th>
                  <th className="num">Saldo Std</th>
                  <th className="num">Saldo Url.</th>
                </tr>
              </thead>
              <tbody>
                {monate.length === 0 && (
                  <tr><td colSpan={12} className="anz-empty">Keine Daten</td></tr>
                )}
                {monate.map((m) => (
                  <tr key={`${m.jahr}-${m.monat}`}>
                    <td><span className="anz-monat-label">{MONTHS_DE[m.monat - 1]}</span></td>
                    <td className="num">{m.erbracht_h?.toFixed(2) ?? "–"}</td>
                    <td className="num">{m.feiertage > 0 ? m.feiertage : "–"}</td>
                    <td className="num">{m.krank > 0 ? <span className="badge-krank">{m.krank}</span> : "–"}</td>
                    <td className="num">{m.urlaub > 0 ? <span className="badge-urlaub">{m.urlaub}</span> : "–"}</td>
                    <td className="num">{m.gutschrift_h?.toFixed(1) ?? "–"}</td>
                    <td className="num">{m.summe_h?.toFixed(2) ?? "–"}</td>
                    <td className="num">{m.soll_arbeitstage ?? "–"}</td>
                    <td className="num">{m.soll_h != null ? m.soll_h.toFixed(1) : "–"}</td>
                    <td className="num">{m.diff_h != null ? (
                      <span className={m.diff_h >= 0 ? "b-pos" : "b-neg"}>{m.diff_h >= 0 ? "+" : ""}{m.diff_h.toFixed(2)}</span>
                    ) : "–"}</td>
                    <td className="num">{m.saldo_h != null ? (
                      <strong className={m.saldo_h >= 0 ? "b-pos" : "b-neg"}>{m.saldo_h.toFixed(2)}</strong>
                    ) : "–"}</td>
                    <td className="num">{m.saldo_urlaub != null ? m.saldo_urlaub.toFixed(1) : "–"}</td>
                  </tr>
                ))}
                {monate.length > 0 && monateMeta?.vorjahr && (
                  <tr style={{opacity:0.85}}>
                    <td><em>Vorjahr / Übertrag</em></td>
                    <td className="num" colSpan={9}></td>
                    <td className="num"><strong>{(monateMeta.vorjahr.saldo_h ?? 0).toFixed(2)}</strong></td>
                    <td className="num"><strong>{(monateMeta.vorjahr.saldo_urlaub ?? 0).toFixed(1)}</strong></td>
                  </tr>
                )}
              </tbody>
              {monate.length > 0 && (
                <tfoot>
                  <tr className="anz-total-row">
                    <td><strong>Gesamt</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.erbracht_h||0), 0).toFixed(2)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.feiertage||0), 0)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.krank||0), 0)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.urlaub||0), 0)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.gutschrift_h||0), 0).toFixed(1)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.summe_h||0), 0).toFixed(2)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.soll_arbeitstage||0), 0)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.soll_h||0), 0).toFixed(1)}</strong></td>
                    <td className="num"><strong>{monate.reduce((s,m) => s + (m.diff_h||0), 0).toFixed(2)}</strong></td>
                    <td className="num"><strong>{monateMeta?.saldo_gesamt != null ? monateMeta.saldo_gesamt.toFixed(2) : (monate[0]?.saldo_h != null ? monate[0].saldo_h.toFixed(2) : "–")}</strong></td>
                    <td className="num"><strong>{monateMeta?.saldo_urlaub != null ? monateMeta.saldo_urlaub.toFixed(1) : "–"}</strong></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="anz-stat-cards">
            {jahresuebersicht.map((j) => (
              <div key={j.jahr} className="anz-stat-year-card">
                <div className="anz-stat-year-title">{j.jahr}</div>
                <div className="anz-stat-year-row">
                  <span className="anz-stat-year-label">Stunden</span>
                  <span className="anz-stat-year-val">{j.stunden?.toFixed(0)} h</span>
                </div>
                <div className="anz-stat-year-row">
                  <span className="anz-stat-year-label">Arbeitstage</span>
                  <span className="anz-stat-year-val">{j.arbeitstage}</span>
                </div>
                <div className="anz-stat-year-row">
                  <span className="anz-stat-year-label">Krank</span>
                  <span className="anz-stat-year-val krank">{j.krank_tage} Tage</span>
                </div>
                <div className="anz-stat-year-row">
                  <span className="anz-stat-year-label">Urlaub</span>
                  <span className="anz-stat-year-val urlaub">{j.urlaub_tage} Tage</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: RELEASES ── */}
      {tab === "releases" && (
        <div className="anz-tab-content">
          <h2 className="anz-section-title">Release-Historie</h2>
          <div className="anz-releases-list">
            {releases.length === 0 && <p className="anz-empty">Keine Releases gefunden.</p>}
            {releases.map((r) => (
              <div key={r.id} className="anz-release-card">
                <div className="anz-release-version">v{r.release_version}</div>
                <div className="anz-release-body">
                  <div className="anz-release-title">{r.was || "–"}</div>
                  {r.datum_beschreibung && (
                    <div className="anz-release-desc">{r.datum_beschreibung}</div>
                  )}
                </div>
                <div className="anz-release-date">{r.datum}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {toast && (
        <div className={`anz-toast anz-toast-${toast.type}`}>{toast.msg}</div>
      )}

      {/* ── TAB: ADMIN ── */}
      {tab === "admin" && (
        <div className="anz-tab-content">
          <div className="admin-subtabs">
            {[["mitarbeiter","Mitarbeiter"],["antraege","📨 Anträge"],["konfiguration","Konfiguration"],["module","🔒 Module"],["statistiken","📊 Statistiken"],["export","⚙ Export"],["import","📥 Import"]].map(([id, label]) => (
              <button key={id}
                className={`admin-subtab-btn ${adminSubTab === id ? "active" : ""}`}
                onClick={() => setAdminSubTab(id)}>{label}</button>
            ))}
          </div>

          {/* ── Sub-Tab: Mitarbeiter (Liste) ── */}
          {adminSubTab === "mitarbeiter" && !adminDetailMaId && (
            <div className="admin-section">
              <div className="admin-section-header" style={{flexWrap:"wrap", gap:10}}>
                <h3 className="anz-section-title" style={{margin:0}}>
                  Mitarbeiter <span className="admin-count-badge">{adminMitarbeiter.length}</span>
                </h3>
                <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
                  <input
                    type="search"
                    className="anz-select"
                    placeholder="Suchen (Name, Kürzel, E-Mail)…"
                    value={adminMaSearch}
                    onChange={(e) => setAdminMaSearch(e.target.value)}
                    style={{minWidth:240}}
                  />
                  <select
                    className="anz-select"
                    value={adminMaFilter}
                    onChange={(e) => setAdminMaFilter(e.target.value)}
                  >
                    <option value="aktiv">Nur aktive</option>
                    <option value="inaktiv">Nur inaktive</option>
                    <option value="alle">Alle anzeigen</option>
                  </select>
                  <button className="anz-btn admin-btn-new" onClick={openAdminMaNew}>+ Neuer Mitarbeiter</button>
                </div>
              </div>
              {adminMaLoading && <div className="anz-loading">Lade …</div>}
              <div className="tbl-wrap">
                <table className="tbl">
                  <colgroup>
                    <col style={{width:"8%"}} />
                    <col style={{width:"20%"}} />
                    <col style={{width:"22%"}} />
                    <col style={{width:"10%"}} />
                    <col style={{width:"14%"}} />
                    <col style={{width:"9%"}} />
                    <col style={{width:"8%"}} />
                    <col style={{width:"9%"}} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Kürzel</th>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Rolle</th>
                      <th>Zugang</th>
                      <th>Bundesland</th>
                      <th className="r">Aktiv</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminMitarbeiterFiltered.length === 0 && !adminMaLoading && (
                      <tr><td colSpan={8} className="tbl-empty">
                        {adminMaSearch ? "Kein Treffer für die Suche" : "Keine Mitarbeiter"}
                      </td></tr>
                    )}
                    {adminMitarbeiterFiltered.map((ma) => (
                      <tr key={ma.id} className="ma-row-clickable"
                          onClick={() => setAdminDetailMaId(ma.id)}
                          title="Details ansehen">
                        <td><strong>{ma.kuerzel}</strong></td>
                        <td>{ma.vorname} {ma.nachname}</td>
                        <td>{ma.email || "–"}</td>
                        <td><span className={`admin-rolle-badge admin-rolle-${ma.rolle}`}>{ma.rolle}</span></td>
                        <td>
                          <span className={`admin-zugang-badge admin-zugang-${ma.zugang_typ || "vollzugang"}`}>
                            {ma.zugang_typ === "nur_zeiterfassung" ? "Zeit-App" : "Vollzugang"}
                          </span>
                        </td>
                        <td title={BUNDESLAENDER[ma.bundesland] || ""}>{ma.bundesland || "–"}</td>
                        <td className="r">{ma.aktiv !== false ? "✓" : "✗"}</td>
                        <td className="td-edit" onClick={(e) => e.stopPropagation()}>
                          <button className="anz-edit-btn" onClick={() => openAdminMaEdit(ma)}>✎</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Sub-Tab: Mitarbeiter (Detail-Ansicht) ── */}
          {adminSubTab === "mitarbeiter" && adminDetailMaId && (
            <AdminMaDetail
              ma={adminDetailMa}
              jahr={adminDetailJahr}
              monat={adminDetailMonat}
              setJahr={setAdminDetailJahr}
              setMonat={setAdminDetailMonat}
              saldo={adminDetailSaldo}
              uebersicht={adminDetailUebersicht}
              eintraege={adminDetailEintraege}
              antraege={adminDetailAntraege}
              loading={adminDetailLoading}
              detailView={adminDetailView}
              setDetailView={setAdminDetailView}
              onBack={() => { setAdminDetailMaId(null); setAdminDetailSaldo(null); setAdminDetailUebersicht(null); setAdminDetailEintraege([]); setAdminDetailAntraege([]); }}
              onEdit={() => adminDetailMa && openAdminMaEdit(adminDetailMa)}
              onEntscheiden={adminEntscheiden}
              onExport={openMaExportDialog}
            />
          )}

          {/* ── Sub-Tab: Anträge (Admin) ── */}
          {adminSubTab === "antraege" && (
            <div className="admin-section">
              <div className="admin-section-header" style={{flexWrap:"wrap",gap:8}}>
                <h3 className="anz-section-title" style={{margin:0}}>
                  Anträge <span className="admin-count-badge">{antraegeAdmin.length}</span>
                </h3>
                <div className="admin-konfig-tabs" style={{margin:0, padding:2}}>
                  {[["offen","Offen"],["genehmigt","Genehmigt"],["abgelehnt","Abgelehnt"],["alle","Alle"]].map(([id, label]) => (
                    <button key={id}
                      className={`admin-konfig-tab ${antrAdminFilter === id ? "active" : ""}`}
                      onClick={() => setAntrAdminFilter(id)}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="tbl-wrap" style={{marginTop:12}}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Mitarbeiter</th>
                      <th>Art</th>
                      <th>Von</th>
                      <th>Bis</th>
                      <th className="r">WT</th>
                      <th>Anmerkung</th>
                      <th>Status</th>
                      <th>Eingereicht</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {antraegeAdmin.length === 0 && (
                      <tr><td colSpan={9} className="tbl-empty">
                        {antrAdminFilter === "offen" ? "Keine offenen Anträge" : "Keine Anträge"}
                      </td></tr>
                    )}
                    {antraegeAdmin.map((a) => (
                      <tr key={a.id}>
                        <td><strong>{a.ma_kuerzel}</strong> · {a.ma_name}</td>
                        <td><span className={`badge badge-antrag-${a.art.toLowerCase()}`}>{a.art}</span></td>
                        <td>{a.von}</td>
                        <td>{a.bis}</td>
                        <td className="r">{a.anzahl_tage}</td>
                        <td style={{maxWidth:240}}>{a.anmerkung || "–"}</td>
                        <td>
                          <span className={`badge badge-antrag-status-${a.status}`}>{a.status}</span>
                          {a.entscheidung_von_kuerzel && (
                            <div style={{fontSize:11, color:"var(--color-gray-500)", marginTop:2}}>
                              durch {a.entscheidung_von_kuerzel}
                            </div>
                          )}
                        </td>
                        <td style={{fontSize:12}}>{a.created_at?.slice(0,16)}</td>
                        <td>
                          {a.status === "offen" ? (
                            <div style={{display:"flex", gap:6}}>
                              <button className="anz-btn anz-btn-sm anz-btn-save"
                                onClick={() => adminEntscheiden(a.id, "genehmigen")}>✓ Genehmigen</button>
                              <button className="anz-btn anz-btn-sm admin-bulk-btn-danger"
                                onClick={() => adminEntscheiden(a.id, "ablehnen")}>✗ Ablehnen</button>
                            </div>
                          ) : a.entscheidung_kommentar ? (
                            <div style={{fontSize:11, color:"var(--color-gray-500)"}}>{a.entscheidung_kommentar}</div>
                          ) : "–"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Sub-Tab: Konfiguration ── */}
          {adminSubTab === "konfiguration" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="anz-section-title" style={{margin:0}}>Konfiguration</h3>
                <select className="anz-select" value={adminKonfigMaId}
                  onChange={e => { setAdminKonfigMaId(e.target.value); setAdminShowNewProjekt(false); setAdminShowNewFeld(false); setAdminShowNewBereich(false); }}>
                  <option value="">– Mitarbeiter wählen –</option>
                  {adminMitarbeiter.map(ma => (
                    <option key={ma.id} value={ma.id}>{ma.kuerzel} – {ma.vorname} {ma.nachname}</option>
                  ))}
                </select>
              </div>

              {!adminKonfigMaId && <p className="admin-hint">Bitte einen Mitarbeiter auswählen.</p>}
              {adminKonfigMaId && adminKonfigLoading && <div className="anz-loading">Lade …</div>}

              {adminKonfigMaId && !adminKonfigLoading && (
                <div className="admin-konfig-content">
                  {/* Sub-Sub-Tab Navigation: Projekte | Felder | Bereiche | Orte
                      So bleiben einzelne Sektionen kompakt und navigierbar. */}
                  <div className="admin-konfig-tabs">
                    {[
                      ["projekte",  "Projekte",  adminKonfig.projekte.length],
                      ["felder",    "Felder",    adminKonfig.felder.length],
                      ["bereiche",  "Bereiche",  adminKonfig.bereiche.length],
                      ["orte",      "Orte",      (adminOrte || []).length],
                      ["export",    "Export-Spalten", adminExportSpalten.length],
                      ["vorlagen",  "Vorlagen",  adminWotagDefaults.filter((d) => d.von || d.art || d.bereiche?.length).length],
                    ].map(([id, label, count]) => (
                      <button key={id}
                        className={`admin-konfig-tab ${adminKonfigSection === id ? "active" : ""}`}
                        onClick={() => setAdminKonfigSection(id)}>
                        {label}
                        <span className="admin-konfig-tab-count">{count}</span>
                      </button>
                    ))}
                  </div>

                  {/* Projekte */}
                  {adminKonfigSection === "projekte" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Projekte / Objekte</h4>
                      <button className="anz-btn anz-btn-sm"
                        onClick={() => {
                          if (adminShowNewProjekt) { setAdminEditProjektIdx(null); setAdminShowNewProjekt(false); setAdminNewProjekt({ label: "", kuerzel: "", sort_order: 0 }); }
                          else { setAdminShowNewProjekt(true); }
                        }}>
                        {adminShowNewProjekt ? "Abbrechen" : "+ Hinzufügen"}
                      </button>
                    </div>
                    {adminShowNewProjekt && (
                      <div className="admin-inline-form">
                        <input className="anz-modal-input" placeholder="Label *" value={adminNewProjekt.label}
                          onChange={e => setAdminNewProjekt(v => ({...v, label: e.target.value}))} />
                        <input className="anz-modal-input" placeholder="Kürzel" value={adminNewProjekt.kuerzel}
                          onChange={e => setAdminNewProjekt(v => ({...v, kuerzel: e.target.value}))} />
                        <input className="anz-modal-input" placeholder="Reihenfolge" type="number" value={adminNewProjekt.sort_order}
                          onChange={e => setAdminNewProjekt(v => ({...v, sort_order: +e.target.value}))} style={{width:110}} />
                        <button className="anz-btn anz-btn-save anz-btn-sm" onClick={adminAddProjekt}>
                          {adminEditProjektIdx !== null ? "Speichern" : "Hinzufügen"}
                        </button>
                      </div>
                    )}
                    <div className="tbl-wrap" style={{marginTop:8}}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th>Kürzel</th>
                            <th className="r">Reihenfolge</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminKonfig.projekte.length === 0 && (
                            <tr><td colSpan={4} className="tbl-empty">Keine Projekte</td></tr>
                          )}
                          {adminKonfig.projekte.map((p, i) => (
                            <tr key={i} className={adminEditProjektIdx === i ? "row-editing" : ""}>
                              <td>{p.label}</td>
                              <td>{p.kuerzel || "–"}</td>
                              <td className="r">{p.sort_order ?? "–"}</td>
                              <td className="td-edit">
                                <button className="anz-edit-btn" onClick={() => adminEditProjekt(i)} title="Bearbeiten">✎</button>
                                <button className="admin-del-btn" onClick={() => adminRemoveProjekt(i)} title="Löschen">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {/* Felder */}
                  {adminKonfigSection === "felder" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Felder</h4>
                      <button className="anz-btn anz-btn-sm"
                        onClick={() => {
                          if (adminShowNewFeld) { setAdminEditFeldIdx(null); setAdminShowNewFeld(false); setAdminNewFeld({ feld_name: "", feld_label: "", feld_typ: "text", optionen: "", sichtbar: true, pflichtfeld: false }); }
                          else { setAdminShowNewFeld(true); }
                        }}>
                        {adminShowNewFeld ? "Abbrechen" : "+ Hinzufügen"}
                      </button>
                    </div>
                    {adminShowNewFeld && (
                      <div className="admin-inline-form">
                        <input className="anz-modal-input" placeholder="Feldname * (intern)"
                          disabled={adminEditFeldIdx !== null}
                          value={adminNewFeld.feld_name}
                          onChange={e => setAdminNewFeld(v => ({...v, feld_name: e.target.value}))} />
                        <input className="anz-modal-input" placeholder="Label (sichtbarer Name)"
                          value={adminNewFeld.feld_label}
                          onChange={e => setAdminNewFeld(v => ({...v, feld_label: e.target.value}))} />
                        <select className="anz-select" value={adminNewFeld.feld_typ}
                          onChange={e => setAdminNewFeld(v => ({...v, feld_typ: e.target.value}))}>
                          <option value="text">Text</option>
                          <option value="checkbox">Checkbox (an/aus)</option>
                          <option value="tristate">Checkbox 3-stufig (aus / ½ / ✓)</option>
                          <option value="select">Select (eine Option)</option>
                          <option value="multiselect">Multiselect (mehrere)</option>
                          <option value="number">Zahl</option>
                          <option value="decimal">Zahl (0.5-Schritte)</option>
                        </select>
                        {(adminNewFeld.feld_typ === "select" || adminNewFeld.feld_typ === "multiselect") && (
                          <input className="anz-modal-input"
                            placeholder="Optionen (kommagetrennt: Küche, Bad, …)"
                            value={adminNewFeld.optionen || ""}
                            onChange={(e) => setAdminNewFeld((v) => ({...v, optionen: e.target.value}))}
                            style={{minWidth: 260}} />
                        )}
                        <label className="admin-check-label">
                          <input type="checkbox" checked={adminNewFeld.sichtbar}
                            onChange={e => setAdminNewFeld(v => ({...v, sichtbar: e.target.checked}))} /> Sichtbar
                        </label>
                        <label className="admin-check-label">
                          <input type="checkbox" checked={adminNewFeld.pflichtfeld}
                            onChange={e => setAdminNewFeld(v => ({...v, pflichtfeld: e.target.checked}))} /> Pflichtfeld
                        </label>
                        <button className="anz-btn anz-btn-save anz-btn-sm" onClick={adminAddFeld}>
                          {adminEditFeldIdx !== null ? "Speichern" : "Hinzufügen"}
                        </button>
                      </div>
                    )}
                    <div className="tbl-wrap" style={{marginTop:8}}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>Feldname</th>
                            <th>Label</th>
                            <th>Typ</th>
                            <th>Optionen</th>
                            <th className="r">Sichtbar</th>
                            <th className="r">Pflicht</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminKonfig.felder.length === 0 && (
                            <tr><td colSpan={7} className="tbl-empty">Keine Felder</td></tr>
                          )}
                          {adminKonfig.felder.map((f, i) => {
                            let opts = "–";
                            if (f.optionen) {
                              try {
                                const arr = typeof f.optionen === "string" ? JSON.parse(f.optionen) : f.optionen;
                                if (Array.isArray(arr)) opts = arr.join(", ");
                              } catch {}
                            }
                            return (
                              <tr key={i} className={adminEditFeldIdx === i ? "row-editing" : ""}>
                                <td>{f.feld_name}</td>
                                <td>{f.feld_label || "–"}</td>
                                <td>{f.feld_typ || "text"}</td>
                                <td style={{maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis"}}>{opts}</td>
                                <td className="r">{f.sichtbar !== false ? "✓" : "–"}</td>
                                <td className="r">{f.pflichtfeld ? "✓" : "–"}</td>
                                <td className="td-edit">
                                  <button className="anz-edit-btn" onClick={() => adminEditFeld(i)} title="Bearbeiten">✎</button>
                                  <button className="admin-del-btn" onClick={() => adminRemoveFeld(i)} title="Löschen">✕</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {/* Bereiche */}
                  {adminKonfigSection === "bereiche" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Bereiche</h4>
                      <button className="anz-btn anz-btn-sm"
                        onClick={() => {
                          if (adminShowNewBereich) { setAdminEditBereichIdx(null); setAdminShowNewBereich(false); setAdminNewBereich({ bereich_label: "", sichtbar: true }); }
                          else { setAdminShowNewBereich(true); }
                        }}>
                        {adminShowNewBereich ? "Abbrechen" : "+ Hinzufügen"}
                      </button>
                    </div>
                    {adminShowNewBereich && (
                      <div className="admin-inline-form">
                        <input className="anz-modal-input" placeholder="Bereich-Label *" value={adminNewBereich.bereich_label}
                          onChange={e => setAdminNewBereich(v => ({...v, bereich_label: e.target.value}))} />
                        <label className="admin-check-label">
                          <input type="checkbox" checked={adminNewBereich.sichtbar}
                            onChange={e => setAdminNewBereich(v => ({...v, sichtbar: e.target.checked}))} /> Sichtbar
                        </label>
                        <button className="anz-btn anz-btn-save anz-btn-sm" onClick={adminAddBereich}>
                          {adminEditBereichIdx !== null ? "Speichern" : "Hinzufügen"}
                        </button>
                      </div>
                    )}
                    <div className="tbl-wrap" style={{marginTop:8}}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th className="r">Sichtbar</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminKonfig.bereiche.length === 0 && (
                            <tr><td colSpan={3} className="tbl-empty">Keine Bereiche</td></tr>
                          )}
                          {adminKonfig.bereiche.map((b, i) => (
                            <tr key={i} className={adminEditBereichIdx === i ? "row-editing" : ""}>
                              <td>{b.bereich_label}</td>
                              <td className="r">{b.sichtbar !== false ? "✓" : "–"}</td>
                              <td className="td-edit">
                                <button className="anz-edit-btn" onClick={() => adminEditBereich(i)} title="Bearbeiten">✎</button>
                                <button className="admin-del-btn" onClick={() => adminRemoveBereich(i)} title="Löschen">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {/* Orte */}
                  {adminKonfigSection === "orte" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Arbeits-Orte</h4>
                    </div>
                    <div className="admin-inline-form" style={{marginBottom:8}}>
                      <input className="anz-modal-input" placeholder="Neuer Ort *" value={adminNewOrt}
                        onChange={e => setAdminNewOrt(e.target.value)} />
                      <button className="anz-btn anz-btn-save anz-btn-sm" onClick={() => {
                        if (!adminNewOrt.trim()) return;
                        setAdminOrte(v => [...v, { ort_label: adminNewOrt.trim(), sichtbar: 1, sort_order: v.length }]);
                        setAdminNewOrt("");
                      }}>Hinzufügen</button>
                    </div>
                    <div className="tbl-wrap" style={{marginTop:4}}>
                      <table className="tbl">
                        <thead><tr><th>Ort</th><th className="r">Sichtbar</th><th></th></tr></thead>
                        <tbody>
                          {adminOrte.length === 0 && <tr><td colSpan={3} className="tbl-empty">Keine Orte</td></tr>}
                          {adminOrte.map((o, i) => (
                            <tr key={i}>
                              <td>{o.ort_label}</td>
                              <td className="r">
                                <input type="checkbox" checked={!!o.sichtbar}
                                  onChange={e => setAdminOrte(v => v.map((x,j) => j===i ? {...x, sichtbar: e.target.checked?1:0} : x))} />
                              </td>
                              <td className="td-edit">
                                <button className="admin-del-btn" onClick={() => setAdminOrte(v => v.filter((_,j)=>j!==i))} title="Löschen">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button className="anz-btn anz-btn-save anz-btn-sm" style={{marginTop:8}} onClick={saveAdminOrte} disabled={adminOrteSaving}>
                      {adminOrteSaving ? "Speichert …" : "Orte speichern"}
                    </button>
                  </div>
                  )}

                  {/* Export-Spalten (Excel-Monatsmappe) */}
                  {adminKonfigSection === "export" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Export-Spalten</h4>
                      <span className="re-muted" style={{fontSize:12, color:"var(--color-gray-500)"}}>
                        Welche Spalten die Excel-Monatsübersicht dieses Mitarbeiters zeigt
                        (Datum und Wochentag sind immer dabei). Beispiel: bei Stundenlöhnern
                        Zusatzfelder AN, Epic/Tätigkeit AUS.
                      </span>
                    </div>
                    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(240px, 1fr))", gap:8, marginTop:10}}>
                      {adminExportSpaltenKeys.map((key) => (
                        <label key={key} className="admin-check-label" style={{display:"flex", alignItems:"center", gap:8}}>
                          <input type="checkbox"
                            checked={adminExportSpalten.includes(key)}
                            onChange={(e) => setAdminExportSpalten((v) =>
                              e.target.checked ? [...v, key] : v.filter((x) => x !== key))} />
                          {EXPORT_SPALTEN_LABELS[key] || key}
                        </label>
                      ))}
                    </div>
                    <button className="anz-btn anz-btn-save anz-btn-sm" style={{marginTop:14}}
                      onClick={saveAdminExportSpalten} disabled={adminExportSpaltenSaving}>
                      {adminExportSpaltenSaving ? "Speichert …" : "Export-Spalten speichern"}
                    </button>
                  </div>
                  )}

                  {/* Vorlagen (Wochentag-Defaults) */}
                  {adminKonfigSection === "vorlagen" && (
                  <div className="admin-konfig-section">
                    <div className="admin-konfig-section-header">
                      <h4 className="admin-konfig-title">Wochentag-Vorlagen</h4>
                      <span className="re-muted" style={{fontSize:12, color:"var(--color-gray-500)"}}>
                        Pre-Fill für die Zeiterfassung — wird nicht automatisch eingetragen, der Mitarbeiter bestätigt jeden Tag selbst.
                      </span>
                    </div>
                    <div className="tbl-wrap" style={{marginTop:8}}>
                      <table className="tbl wochentag-table">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>Art</th>
                            <th>Von</th>
                            <th>Bis</th>
                            <th>Pause (Min)</th>
                            <th>Ort</th>
                            <th>Projekt</th>
                            <th>Aktiv</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminWotagDefaults.map((d, i) => (
                            <tr key={i} className={d.wotag >= 5 ? "row-we" : ""}>
                              <td><strong>{d.wotag_label}</strong></td>
                              <td>
                                <select className="anz-select"
                                  value={d.art || ""}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, art: e.target.value} : x))}>
                                  <option value="">– kein –</option>
                                  <option value="Arbeit">Arbeit</option>
                                  <option value="Krank">Krank</option>
                                  <option value="Urlaub">Urlaub</option>
                                </select>
                              </td>
                              <td>
                                <input type="time" step="900" className="anz-modal-input" value={d.von || ""}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, von: e.target.value} : x))} />
                              </td>
                              <td>
                                <input type="time" step="900" className="anz-modal-input" value={d.bis || ""}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, bis: e.target.value} : x))} />
                              </td>
                              <td>
                                <input type="number" min="0" max="240" className="anz-modal-input"
                                  value={d.pause_min ?? ""}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, pause_min: e.target.value ? +e.target.value : null} : x))}
                                  style={{width:80}} />
                              </td>
                              <td>
                                <input type="text" className="anz-modal-input"
                                  value={d.arbeitsort || ""}
                                  list={`orte-${i}`}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, arbeitsort: e.target.value} : x))} />
                                <datalist id={`orte-${i}`}>
                                  {(adminOrte || []).map((o) => <option key={o.ort_label} value={o.ort_label} />)}
                                </datalist>
                              </td>
                              <td>
                                <select className="anz-select"
                                  value={d.projekt_kuerzel || ""}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, projekt_kuerzel: e.target.value} : x))}>
                                  <option value="">– kein –</option>
                                  {(adminKonfig.projekte || []).map((p) => (
                                    <option key={p.kuerzel || p.label} value={p.kuerzel || p.label}>
                                      {p.label} {p.kuerzel ? `(${p.kuerzel})` : ""}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="r">
                                <input type="checkbox" checked={d.aktiv !== false}
                                  onChange={(e) => setAdminWotagDefaults(v => v.map((x, j) => j === i ? {...x, aktiv: e.target.checked} : x))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button className="anz-btn anz-btn-save anz-btn-sm" style={{marginTop:8}}
                      onClick={saveAdminWotagDefaults} disabled={adminWotagSaving}>
                      {adminWotagSaving ? "Speichert …" : "Vorlagen speichern"}
                    </button>
                  </div>
                  )}

                  {/* Globaler Speichern-Button (Projekte/Felder/Bereiche zusammen) */}
                  {adminKonfigSection !== "orte" && adminKonfigSection !== "vorlagen" && (
                    <div className="admin-konfig-save">
                      <button className="anz-btn anz-btn-save" onClick={handleAdminKonfigSave} disabled={adminKonfigSaving}>
                        {adminKonfigSaving ? "Speichert …" : "Projekte/Felder/Bereiche speichern"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab: Module ── */}
          {adminSubTab === "module" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="anz-section-title" style={{margin:0}}>Modul-Freigaben</h3>
                <select className="anz-select" value={adminModulMaId}
                  onChange={e => { setAdminModulMaId(e.target.value); if (e.target.value) loadAdminModule(e.target.value); }}>
                  <option value="">– Mitarbeiter wählen –</option>
                  {adminMitarbeiter.map(ma => (
                    <option key={ma.id} value={ma.id}>{ma.kuerzel} – {ma.vorname} {ma.nachname}</option>
                  ))}
                </select>
              </div>
              {!adminModulMaId && <p className="admin-hint">Bitte einen Mitarbeiter auswählen.</p>}
              {adminModulMaId && (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:12}}>
                  {ALLE_MODULE.map(m => (
                    <label key={m} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",background:"var(--bg-card)",padding:"10px 14px",borderRadius:8,border:"1px solid var(--color-gray-200)"}}>
                      <input type="checkbox"
                        checked={adminModulList.includes(m)}
                        onChange={e => {
                          if (e.target.checked) setAdminModulList(l => [...l, m]);
                          else setAdminModulList(l => l.filter(x => x !== m));
                        }}
                        style={{width:16,height:16}}
                      />
                      <span style={{fontWeight:600}}>{m}</span>
                    </label>
                  ))}
                  <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
                    <button
                      className="anz-btn admin-bulk-btn"
                      onClick={() => {
                        if (window.confirm(`Alle ${ALLE_MODULE.length} Module für diesen Mitarbeiter aktivieren?`)) {
                          setAdminModulList([...ALLE_MODULE]);
                        }
                      }}
                    >Alle an</button>
                    <button
                      className="anz-btn admin-bulk-btn admin-bulk-btn-danger"
                      onClick={() => {
                        if (window.confirm(`Alle Module für diesen Mitarbeiter deaktivieren?\n\nDer Mitarbeiter verliert dadurch Zugriff auf alle Funktionen außer Login.`)) {
                          setAdminModulList([]);
                        }
                      }}
                    >Alle aus</button>
                    <button className="anz-btn anz-btn-save" onClick={handleAdminModulSave} disabled={adminModulSaving}>
                      {adminModulSaving ? "Speichert …" : "Module speichern"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab: Statistiken ── */}
          {adminSubTab === "statistiken" && (
            <div className="admin-section">
              <div className="admin-section-header" style={{flexWrap:"wrap",gap:8}}>
                <h3 className="anz-section-title" style={{margin:0}}>
                  Statistiken aller Mitarbeiter
                  <span className="admin-count-badge">{adminStatRows.length}</span>
                </h3>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <input
                    type="search"
                    className="anz-select"
                    placeholder="Suchen…"
                    value={adminStatSearch}
                    onChange={(e) => setAdminStatSearch(e.target.value)}
                    style={{minWidth: 180}}
                  />
                  <select className="anz-select" value={adminStatMaId}
                    onChange={e => setAdminStatMaId(e.target.value)}>
                    <option value="">Alle Mitarbeiter</option>
                    {adminMitarbeiter.map(ma => (
                      <option key={ma.id} value={ma.id}>{ma.kuerzel} – {ma.vorname} {ma.nachname}</option>
                    ))}
                  </select>
                  <select className="anz-select" value={adminStatJahr}
                    onChange={e => setAdminStatJahr(+e.target.value)}>
                    <option value="">Alle Jahre</option>
                    {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <div className="admin-konfig-tabs" style={{margin:0, padding:2}}>
                    {[["tabelle","📋 Tabelle"], ["heatmap","🔥 Heatmap"]].map(([id, label]) => (
                      <button key={id}
                        className={`admin-konfig-tab ${adminStatView === id ? "active" : ""}`}
                        onClick={() => setAdminStatView(id)}>{label}</button>
                    ))}
                  </div>
                  <button className="anz-btn anz-btn-export" onClick={doAdminStatExport}>
                    CSV Export
                  </button>
                </div>
              </div>

              {adminStatLoading && <div className="anz-loading">Lade …</div>}

              {/* Tabellen-Ansicht */}
              {!adminStatLoading && adminStatView === "tabelle" && (
                <div className="tbl-wrap" style={{marginTop:12}}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Kürzel</th>
                        <th>Name</th>
                        <th className="r">Jahr</th>
                        <th className="r">Ist (h)</th>
                        <th className="r">Soll (h)</th>
                        <th className="r">±</th>
                        <th className="r">Arbeitstage</th>
                        <th className="r">Krank</th>
                        <th className="r">Urlaub</th>
                        <th className="r">Feiertage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminStatRows.length === 0 && (
                        <tr><td colSpan={10} className="tbl-empty">Keine Daten</td></tr>
                      )}
                      {adminStatRows.map((r, i) => (
                        <tr key={i}>
                          <td><strong>{r.kuerzel}</strong></td>
                          <td>{r.vorname} {r.nachname}</td>
                          <td className="r">{r.jahr}</td>
                          <td className="r">{r.ist.toFixed(1)}</td>
                          <td className="r" style={{color:"var(--color-gray-500)"}}>{r.soll.toFixed(1)}</td>
                          <td className={`r admin-stat-diff ${r.diff >= 0 ? "pos" : "neg"}`}>
                            {r.diff >= 0 ? "+" : ""}{r.diff.toFixed(1)}
                          </td>
                          <td className="r">{r.arbeitstage}</td>
                          <td className="r">{r.krank_tage > 0 ? <span className="badge-krank">{r.krank_tage}</span> : "–"}</td>
                          <td className="r">{r.urlaub_tage > 0 ? <span className="badge-urlaub">{r.urlaub_tage}</span> : "–"}</td>
                          <td className="r">{r.feiertage > 0 ? r.feiertage : "–"}</td>
                        </tr>
                      ))}
                    </tbody>
                    {adminStatRows.length > 0 && (
                      <tfoot className="admin-stat-totals">
                        <tr>
                          <td colSpan={3}><strong>Σ ({adminStatRows.length} Zeilen)</strong></td>
                          <td className="r"><strong>{adminStatTotals.stunden.toFixed(1)}</strong></td>
                          <td className="r" style={{color:"var(--color-gray-500)"}}>{adminStatTotals.soll.toFixed(1)}</td>
                          <td className={`r admin-stat-diff ${adminStatTotals.diff >= 0 ? "pos" : "neg"}`}>
                            <strong>{adminStatTotals.diff >= 0 ? "+" : ""}{adminStatTotals.diff.toFixed(1)}</strong>
                          </td>
                          <td className="r"><strong>{adminStatTotals.arbeitstage}</strong></td>
                          <td className="r"><strong>{adminStatTotals.krank_tage}</strong></td>
                          <td className="r"><strong>{adminStatTotals.urlaub_tage}</strong></td>
                          <td className="r"><strong>{adminStatTotals.feiertage}</strong></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}

              {/* Heatmap-Ansicht: Balken pro Mitarbeiter im Verhältnis zum Soll */}
              {!adminStatLoading && adminStatView === "heatmap" && (
                <div className="admin-stat-heatmap" style={{marginTop:12}}>
                  {adminStatRows.length === 0 && <p className="tbl-empty">Keine Daten</p>}
                  {adminStatRows.map((r, i) => {
                    const ratio = r.soll > 0 ? (r.ist / r.soll) : 0;
                    const widthPct = Math.min(120, Math.max(0, ratio * 100));
                    const color = ratio >= 0.95 && ratio <= 1.05 ? "ok"
                      : ratio < 0.95 ? "warn"
                      : "over";
                    return (
                      <div key={i} className="admin-stat-row">
                        <div className="admin-stat-label">
                          <strong>{r.kuerzel}</strong> · {r.vorname} {r.nachname} · {r.jahr}
                        </div>
                        <div className="admin-stat-bar-wrap">
                          {/* 100%-Marker */}
                          <div className="admin-stat-bar-100" />
                          <div className={`admin-stat-bar admin-stat-bar-${color}`} style={{width: `${widthPct}%`}} />
                          <div className="admin-stat-bar-text">
                            {r.ist.toFixed(0)} / {r.soll.toFixed(0)} h
                            <span className={`admin-stat-diff ${r.diff >= 0 ? "pos" : "neg"}`} style={{marginLeft:8}}>
                              {r.diff >= 0 ? "+" : ""}{r.diff.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        <div className="admin-stat-extras">
                          <span>{r.arbeitstage} AT</span>
                          {r.krank_tage > 0 && <span className="badge-krank">{r.krank_tage} K</span>}
                          {r.urlaub_tage > 0 && <span className="badge-urlaub">{r.urlaub_tage} U</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab: Export-Konfiguration ── */}
          {adminSubTab === "export" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="anz-section-title" style={{margin:0}}>⚙ Export-Konfiguration</h3>
              </div>
              <p className="admin-hint">Welche Spalten sollen im Excel/CSV-Export enthalten sein?</p>
              {exportConfig && (
                <div className="admin-konfig-section">
                  {EXPORT_FELDER_ALL.map(feld => (
                    <label key={feld} className="admin-check-label" style={{display:"block",marginBottom:6}}>
                      <input type="checkbox"
                        checked={exportConfig.spalten?.includes(feld)}
                        onChange={e => setExportConfig(c => ({
                          ...c,
                          spalten: e.target.checked
                            ? [...(c.spalten || []), feld]
                            : (c.spalten || []).filter(s => s !== feld)
                        }))}
                        style={{marginRight:8}} />
                      {feld}
                    </label>
                  ))}
                  <button className="anz-btn anz-btn-save" style={{marginTop:16}} onClick={saveExportConfig} disabled={exportConfigSaving}>
                    {exportConfigSaving ? "Speichert …" : "Konfiguration speichern"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab: Import-Wizard ── */}
          {adminSubTab === "import" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="anz-section-title" style={{margin:0}}>📥 Excel-Import</h3>
              </div>

              {/* Schritt 1: Datei + MA wählen */}
              {importStep === 1 && (
                <div className="admin-konfig-section">
                  <p className="admin-hint">Excel-Datei hochladen und Mitarbeiter auswählen, dann Spalten mappen.</p>
                  <div className="anz-modal-field" style={{marginBottom:12}}>
                    <label>Mitarbeiter *</label>
                    <select className="anz-select" value={importMaId} onChange={e => setImportMaId(e.target.value)}>
                      <option value="">– Mitarbeiter wählen –</option>
                      {adminMitarbeiter.map(ma => (
                        <option key={ma.id} value={ma.id}>{ma.kuerzel} – {ma.vorname} {ma.nachname}</option>
                      ))}
                    </select>
                  </div>
                  <div className="anz-modal-field" style={{marginBottom:16}}>
                    <label>Excel-Datei (.xlsx) *</label>
                    <input type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files[0])} />
                  </div>
                  <button className="anz-btn anz-btn-save" onClick={handleImportPreview}
                    disabled={!importFile || !importMaId || importLoading}>
                    {importLoading ? "Lade Vorschau …" : "Weiter → Spalten mappen"}
                  </button>
                </div>
              )}

              {/* Schritt 2: Mapping */}
              {importStep === 2 && importPreview && (
                <div className="admin-konfig-section">
                  <p className="admin-hint">Ordne jede Excel-Spalte dem richtigen Feld zu. Nicht benötigte Spalten auf „— ignorieren —" lassen.</p>
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Excel-Spalte</th>
                          <th>Vorschau</th>
                          <th>Feld</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.spalten.map((s, i) => (
                          <tr key={i}>
                            <td><strong>{s}</strong></td>
                            <td style={{fontSize:"0.8em",color:"var(--color-gray-500)"}}>
                              {importPreview.vorschau.slice(0,3).map(row => row[i]).filter(Boolean).join(" / ")}
                            </td>
                            <td>
                              <select className="anz-select" value={importMapping[i] || ""}
                                onChange={e => setImportMapping(m => ({...m, [i]: e.target.value}))}>
                                <option value="">— ignorieren —</option>
                                {importPreview.felder.map(f => <option key={f} value={f}>{f}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:16}}>
                    <button className="anz-btn anz-btn-cancel" onClick={() => { setImportStep(1); setImportPreview(null); }}>Zurück</button>
                    <button className="anz-btn anz-btn-save" onClick={handleImportExecute}
                      disabled={importLoading || !Object.values(importMapping).some(v=>v==="datum")}>
                      {importLoading ? "Importiert …" : `${importPreview.total_rows} Zeilen importieren`}
                    </button>
                  </div>
                  {!Object.values(importMapping).some(v=>v==="datum") && (
                    <p style={{color:"var(--color-warning)",marginTop:8,fontSize:"0.85em"}}>⚠ Spalte „datum" muss gemappt sein.</p>
                  )}
                </div>
              )}

              {/* Schritt 3: Ergebnis */}
              {importStep === 3 && importResult && (
                <div className="admin-konfig-section">
                  <div style={{padding:"20px",textAlign:"center"}}>
                    <div style={{fontSize:"2em",marginBottom:8}}>✅</div>
                    <h4>Import abgeschlossen</h4>
                    <p>Neu eingefügt: <strong>{importResult.inserted}</strong></p>
                    <p>Aktualisiert: <strong>{importResult.updated}</strong></p>
                    <p>Übersprungen: <strong>{importResult.skipped}</strong></p>
                    <button className="anz-btn anz-btn-save" style={{marginTop:16}} onClick={() => {
                      setImportStep(1); setImportFile(null); setImportPreview(null); setImportResult(null); setImportMapping({});
                    }}>Neuer Import</button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* ── Admin MA Modal ── */}
      {adminMaModal && (
        <div className="anz-modal-overlay" onClick={() => setAdminMaModal(null)}>
          <div className="anz-modal admin-ma-modal" onClick={e => e.stopPropagation()}>
            <div className="anz-modal-header">
              <h3 className="anz-modal-title">
                {adminMaModal.mode === "new" ? "Neuer Mitarbeiter" : `Bearbeiten: ${adminMaModal.ma?.kuerzel}`}
              </h3>
            </div>
            <div className="anz-modal-grid">
              {adminMaModal.mode === "new" && (
                <div className="anz-modal-field">
                  <label>Kürzel *</label>
                  <input className="anz-modal-input" value={adminMaForm.kuerzel}
                    onChange={e => setAdminMaForm(f => ({...f, kuerzel: e.target.value.toUpperCase()}))}
                    placeholder="z.B. LNH" maxLength={6} />
                </div>
              )}
              <div className="anz-modal-field">
                <label>Vorname</label>
                <input className="anz-modal-input" value={adminMaForm.vorname}
                  onChange={e => setAdminMaForm(f => ({...f, vorname: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Nachname</label>
                <input className="anz-modal-input" value={adminMaForm.nachname}
                  onChange={e => setAdminMaForm(f => ({...f, nachname: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>E-Mail</label>
                <input className="anz-modal-input" type="email" value={adminMaForm.email}
                  onChange={e => setAdminMaForm(f => ({...f, email: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Rolle</label>
                <select className="anz-select" value={adminMaForm.rolle}
                  onChange={e => setAdminMaForm(f => ({...f, rolle: e.target.value}))}>
                  <option value="mitarbeiter">Mitarbeiter</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="anz-modal-field">
                <label title="Bestimmt die berücksichtigten Feiertage und damit die Soll-Stunden des Mitarbeiters">
                  Bundesland
                </label>
                <select className="anz-select" value={adminMaForm.bundesland}
                  onChange={e => setAdminMaForm(f => ({...f, bundesland: e.target.value}))}>
                  {Object.entries(BUNDESLAENDER)
                    .sort(([, a], [, b]) => a.localeCompare(b))
                    .map(([bl, name]) => (
                      <option key={bl} value={bl}>{name} ({bl})</option>
                    ))}
                </select>
              </div>
              <div className="anz-modal-field">
                <label>{adminMaModal.mode === "new" ? "Passwort *" : "Passwort (leer = nicht ändern)"}</label>
                <input className="anz-modal-input" type="password" value={adminMaForm.passwort}
                  onChange={e => setAdminMaForm(f => ({...f, passwort: e.target.value}))} />
              </div>
              <div className="anz-modal-field admin-check-field">
                <label className="admin-check-label">
                  <input type="checkbox" checked={adminMaForm.aktiv}
                    onChange={e => setAdminMaForm(f => ({...f, aktiv: e.target.checked}))} />
                  Aktiv
                </label>
              </div>
              <div className="anz-modal-field">
                <label>Zugang</label>
                <select className="anz-select" value={adminMaForm.zugang_typ || "vollzugang"}
                  onChange={e => setAdminMaForm(f => ({...f, zugang_typ: e.target.value}))}>
                  <option value="vollzugang">Vollzugang (IMHO + Zeiterfassung)</option>
                  <option value="nur_zeiterfassung">Nur Zeiterfassung (/zeit App)</option>
                </select>
              </div>
              <div className="anz-modal-field admin-check-field">
                <label className="admin-check-label">
                  <input type="checkbox" checked={!!adminMaForm.zeigt_zeiterfassung}
                    onChange={e => setAdminMaForm(f => ({...f, zeigt_zeiterfassung: e.target.checked ? 1 : 0}))} />
                  Zeiterfassung sichtbar
                </label>
              </div>
              <div className="anz-modal-field">
                <label>Regelarbeitszeit (h/Tag)</label>
                <input className="anz-modal-input" type="number" min="0" max="24" step="0.5"
                  value={adminMaForm.regelarbeitszeit_h}
                  onChange={e => setAdminMaForm(f => ({...f, regelarbeitszeit_h: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Urlaubstage / Jahr</label>
                <input className="anz-modal-input" type="number" min="0" max="365"
                  value={adminMaForm.urlaubstage_pro_jahr}
                  onChange={e => setAdminMaForm(f => ({...f, urlaubstage_pro_jahr: parseInt(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Arbeitstage / Woche</label>
                <input className="anz-modal-input" type="number" min="1" max="7"
                  value={adminMaForm.arbeitstage_woche}
                  onChange={e => setAdminMaForm(f => ({...f, arbeitstage_woche: parseInt(e.target.value) || 5}))} />
              </div>
              <div className="anz-modal-field">
                <label>Reisekosten-Pauschale (€/Tag)</label>
                <input className="anz-modal-input" type="number" min="0" step="0.5"
                  title="Wird im Leistungsnachweis für Tage mit Arbeit außerhalb Homeoffice angesetzt"
                  value={adminMaForm.reisekosten_pauschale ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, reisekosten_pauschale: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Auftragsvolumen (PT)</label>
                <input className="anz-modal-input" type="number" min="0" step="1"
                  title="Vertragsvolumen in Personentagen (z. B. 140) — steuert den Rest-Wert im Soll-Ist-Abgleich der Leistungsnachweis-Excel. 0/leer = kein Soll-Ist-Block."
                  value={adminMaForm.auftragsvolumen_pt ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, auftragsvolumen_pt: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Plan-Tage / Monat (PT)</label>
                <input className="anz-modal-input" type="number" min="0" step="0.5"
                  title="Geplante Personentage pro Monat (z. B. 20) — die 'Plan'-Spalte im Soll-Ist-Abgleich."
                  value={adminMaForm.plan_tage_monat ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, plan_tage_monat: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Vertragsdatum</label>
                <input className="anz-modal-input" type="date"
                  title="Vertragsbeginn — Startmonat des Soll-Ist-Abgleichs und 'vom'-Datum in der Excel."
                  value={adminMaForm.vertragsdatum ?? ""}
                  onChange={e => setAdminMaForm(f => ({...f, vertragsdatum: e.target.value}))} />
              </div>
              <div className="anz-modal-field">
                <label>Wochensoll (h/Woche)</label>
                <input className="anz-modal-input" type="number" min="0" step="0.5"
                  title="Für Stundenlöhner (z. B. 10). Tagessoll = Wochensoll ÷ Arbeitstage/Woche. 0/leer = Regelarbeitszeit gilt."
                  value={adminMaForm.wochensoll_h ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, wochensoll_h: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Saldo-Übertrag (h)</label>
                <input className="anz-modal-input" type="number" step="0.25"
                  title="Stunden-Überhang zum 01.01. des 'Übertrag ab Jahr' (darf negativ sein)"
                  value={adminMaForm.saldo_uebertrag_h ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, saldo_uebertrag_h: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Urlaubs-Übertrag (Tage)</label>
                <input className="anz-modal-input" type="number" step="0.5"
                  title="Urlaubs-Saldo zum 01.01. des 'Übertrag ab Jahr'"
                  value={adminMaForm.urlaub_uebertrag_tage ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, urlaub_uebertrag_tage: parseFloat(e.target.value) || 0}))} />
              </div>
              <div className="anz-modal-field">
                <label>Übertrag ab Jahr</label>
                <input className="anz-modal-input" type="number" min="0" max="2100"
                  title="Ab diesem Jahr startet die Saldo-Kette mit den Übertragswerten; ältere Einträge zählen nicht mehr in den Saldo. 0/leer = ab Datenbeginn."
                  value={adminMaForm.uebertrag_ab_jahr ?? 0}
                  onChange={e => setAdminMaForm(f => ({...f, uebertrag_ab_jahr: parseInt(e.target.value) || 0}))} />
              </div>
            </div>
            <div className="anz-modal-footer">
              <button className="anz-btn anz-btn-cancel" onClick={() => setAdminMaModal(null)}>Abbrechen</button>
              <button className="anz-btn anz-btn-save" onClick={handleAdminMaSave} disabled={adminMaSaving}>
                {adminMaSaving ? "Speichert …" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Export Dialog ── */}
      {exportDialog && (
        <div className="anz-modal-overlay" onClick={() => setExportDialog(false)}>
          <div className="anz-modal exp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="anz-modal-title">CSV Export</h3>

            <div className="exp-row">
              <label className="exp-label">Zeitraum</label>
              <div className="exp-toggle">
                {["monat","jahr","alles"].map((m) => (
                  <button
                    key={m}
                    className={`exp-tog-btn${expModus === m ? " active" : ""}`}
                    onClick={() => setExpModus(m)}
                  >
                    {m === "monat" ? "Monat" : m === "jahr" ? "Jahr" : "Alles"}
                  </button>
                ))}
              </div>
            </div>

            {expModus !== "alles" && (
              <div className="exp-row">
                <label className="exp-label">Jahr</label>
                <select className="anz-select" value={expJahr} onChange={(e) => setExpJahr(+e.target.value)}>
                  {[2024,2025,2026].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}

            {expModus === "monat" && (
              <div className="exp-row">
                <label className="exp-label">Monat</label>
                <select className="anz-select" value={expMonat} onChange={(e) => setExpMonat(+e.target.value)}>
                  {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
            )}

            <div className="exp-row">
              <label className="exp-check-label">
                <input type="checkbox" checked={expZusatz} onChange={(e) => setExpZusatz(e.target.checked)} />
                Zusatz-Einträge einschließen
              </label>
            </div>

            <div className="anz-modal-actions">
              <button className="anz-btn" onClick={() => setExportDialog(false)}>Abbrechen</button>
              <button className="anz-btn anz-btn-export" onClick={doExport}>⬇ Herunterladen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Self-Export (eigener Nachweis, auch Zeit-App) ── */}
      {selfExportDlg && (
        <div className="anz-modal-overlay">
          <div className="anz-modal exp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="anz-modal-title">Export · mein Nachweis</h3>
            <div className="exp-row">
              <label className="exp-label">Dokument-Typ</label>
              <div className="exp-toggle">
                <button className={`exp-tog-btn${selfExportDlg.doctyp === "vv" ? " active" : ""}`}
                        onClick={() => setSelfExportDlg(d => ({ ...d, doctyp: "vv" }))}>
                  VB · Leistungsnachweis (Excel)
                </button>
                <button className={`exp-tog-btn${selfExportDlg.doctyp === "monate" ? " active" : ""}`}
                        onClick={() => setSelfExportDlg(d => ({ ...d, doctyp: "monate" }))}>
                  Excel · Monatsübersicht
                </button>
              </div>
            </div>
            <div className="exp-row">
              <label className="exp-label">Jahr</label>
              <select className="anz-select" value={selfExportDlg.jahr}
                      onChange={(e) => setSelfExportDlg(d => ({ ...d, jahr: +e.target.value }))}>
                {[2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {selfExportDlg.doctyp === "vv" && (
              <>
                <div className="exp-row">
                  <label className="exp-label">Monat</label>
                  <select className="anz-select" value={selfExportDlg.monat}
                          onChange={(e) => setSelfExportDlg(d => ({ ...d, monat: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="exp-row">
                  <label className="exp-label">Firma</label>
                  <input className="anz-select" value={selfExportDlg.firma}
                         placeholder="z. B. Sijelly IT GmbH"
                         onChange={(e) => setSelfExportDlg(d => ({ ...d, firma: e.target.value }))} />
                </div>
                <div className="exp-row">
                  <label className="exp-label">Projekt / Projektname</label>
                  <input className="anz-select" value={selfExportDlg.projekt}
                         onChange={(e) => setSelfExportDlg(d => ({ ...d, projekt: e.target.value }))} />
                </div>
                <div className="exp-row">
                  <label className="exp-label">Bestell. Nr.</label>
                  <input className="anz-select" value={selfExportDlg.bestellnr}
                         onChange={(e) => setSelfExportDlg(d => ({ ...d, bestellnr: e.target.value }))} />
                </div>
                <p className="admin-hint">Alle Kalendertage werden gelistet; nur erfasste Tage befüllt.</p>
              </>
            )}
            {selfExportDlg.doctyp === "monate" && (
              <>
                <div className="exp-row">
                  <label className="exp-label">Umfang</label>
                  <div className="exp-toggle">
                    <button className={`exp-tog-btn${!selfExportDlg.gesamt ? " active" : ""}`}
                            onClick={() => setSelfExportDlg(d => ({ ...d, gesamt: false }))}>
                      Zeitraum im Jahr
                    </button>
                    <button className={`exp-tog-btn${selfExportDlg.gesamt ? " active" : ""}`}
                            onClick={() => setSelfExportDlg(d => ({ ...d, gesamt: true }))}>
                      Gesamt (alle Jahre)
                    </button>
                  </div>
                </div>
                {!selfExportDlg.gesamt && (
                <div className="exp-row">
                  <label className="exp-label">Zeitraum</label>
                  <select className="anz-select" value={selfExportDlg.monat_von}
                          onChange={(e) => setSelfExportDlg(d => ({ ...d, monat_von: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                  <span style={{margin:"0 6px"}}>bis</span>
                  <select className="anz-select" value={selfExportDlg.monat_bis}
                          onChange={(e) => setSelfExportDlg(d => ({ ...d, monat_bis: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                )}
                <p className="admin-hint">{selfExportDlg.gesamt
                  ? "Alles vom ersten Eintrag bis heute – jahresübergreifend, mit Zwischen-Übersicht je Jahr und einer Gesamt-Zeile über alles."
                  : "Erst ein Übersichts-Sheet mit den Kennzahlen je Monat, dann je Monat ein eigenes Sheet mit allen Tagen und der Berechnung darunter."}</p>
              </>
            )}
            <div className="anz-modal-actions">
              <button className="anz-btn" onClick={() => setSelfExportDlg(null)}>Abbrechen</button>
              <button className="anz-btn anz-btn-export" onClick={doSelfExport}>⬇ Herunterladen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin: Export für EINE Person (Doktyp-Auswahl) ── */}
      {maExportDlg && (
        <div className="anz-modal-overlay">
          <div className="anz-modal exp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="anz-modal-title">
              Export · {adminDetailMa?.kuerzel} {adminDetailMa ? `(${adminDetailMa.vorname} ${adminDetailMa.nachname})` : ""}
            </h3>

            <div className="exp-row">
              <label className="exp-label">Dokument-Typ</label>
              <div className="exp-toggle">
                <button className={`exp-tog-btn${maExportDlg.doctyp === "vv" ? " active" : ""}`}
                        onClick={() => setMaExportDlg(d => ({ ...d, doctyp: "vv" }))}>
                  VB · Leistungsnachweis (Excel)
                </button>
                <button className={`exp-tog-btn${maExportDlg.doctyp === "monate" ? " active" : ""}`}
                        onClick={() => setMaExportDlg(d => ({ ...d, doctyp: "monate" }))}>
                  Excel · Monatsübersicht
                </button>
              </div>
            </div>

            <div className="exp-row">
              <label className="exp-label">Jahr</label>
              <span className="anz-select" style={{padding:"6px 10px"}}>{adminDetailJahr}</span>
            </div>

            {maExportDlg.doctyp === "vv" && (
              <>
                <div className="exp-row">
                  <label className="exp-label">Monat</label>
                  <select className="anz-select" value={maExportDlg.monat}
                          onChange={(e) => setMaExportDlg(d => ({ ...d, monat: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="exp-row">
                  <label className="exp-label">Firma</label>
                  <input className="anz-select" value={maExportDlg.firma}
                         placeholder="z. B. Sijelly IT GmbH"
                         onChange={(e) => setMaExportDlg(d => ({ ...d, firma: e.target.value }))} />
                </div>
                <div className="exp-row">
                  <label className="exp-label">Projekt / Projektname</label>
                  <input className="anz-select" value={maExportDlg.projekt}
                         onChange={(e) => setMaExportDlg(d => ({ ...d, projekt: e.target.value }))} />
                </div>
                <div className="exp-row">
                  <label className="exp-label">Bestell. Nr.</label>
                  <input className="anz-select" value={maExportDlg.bestellnr}
                         onChange={(e) => setMaExportDlg(d => ({ ...d, bestellnr: e.target.value }))} />
                </div>
                <p className="admin-hint">Alle Kalendertage werden gelistet; nur erfasste Tage befüllt. Kopf-Angaben landen im Excel-Kopf.</p>
              </>
            )}
            {maExportDlg.doctyp === "monate" && (
              <>
                <div className="exp-row">
                  <label className="exp-label">Umfang</label>
                  <div className="exp-toggle">
                    <button className={`exp-tog-btn${!maExportDlg.gesamt ? " active" : ""}`}
                            onClick={() => setMaExportDlg(d => ({ ...d, gesamt: false }))}>
                      Zeitraum im Jahr
                    </button>
                    <button className={`exp-tog-btn${maExportDlg.gesamt ? " active" : ""}`}
                            onClick={() => setMaExportDlg(d => ({ ...d, gesamt: true }))}>
                      Gesamt (alle Jahre)
                    </button>
                  </div>
                </div>
                {!maExportDlg.gesamt && (
                <div className="exp-row">
                  <label className="exp-label">Zeitraum</label>
                  <select className="anz-select" value={maExportDlg.monat_von}
                          onChange={(e) => setMaExportDlg(d => ({ ...d, monat_von: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                  <span style={{margin:"0 6px"}}>bis</span>
                  <select className="anz-select" value={maExportDlg.monat_bis}
                          onChange={(e) => setMaExportDlg(d => ({ ...d, monat_bis: +e.target.value }))}>
                    {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                )}
                <p className="admin-hint">{maExportDlg.gesamt
                  ? "Alles vom ersten Eintrag bis heute – jahresübergreifend, mit Zwischen-Übersicht je Jahr und einer Gesamt-Zeile über alles."
                  : "Erst ein Übersichts-Sheet mit den Kennzahlen je Monat, dann je Monat ein eigenes Sheet mit allen Tagen und der Berechnung darunter."}</p>
              </>
            )}

            <div className="anz-modal-actions">
              <button className="anz-btn" onClick={() => setMaExportDlg(null)}>Abbrechen</button>
              <button className="anz-btn anz-btn-export" onClick={doMaExport}>⬇ Herunterladen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
